import React from "react";
import axios from "axios";
import Cookies from "universal-cookie";
import { webURL } from "../../services/API";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
   faArrowLeft, faRobot, faWandMagicSparkles, faBrain, faComments,
   faFileExport, faLightbulb, faFlask, faChevronDown, faChevronRight,
   faPlay, faStop, faCheck, faThumbsUp, faThumbsDown,
   faSpinner, faStar, faBookOpen, faCode,
   faArrowRight, faGear,
   faSave, faFolderOpen, faTrash, faPlus, faLayerGroup, faRotateLeft,
   faMagnifyingGlass, faBolt, faClock, faEllipsisVertical, faPen, faClone, faXmark,
   faEnvelope, faCloud, faDatabase, faCalendar, faCloudArrowUp, faCopy, faEarthAmericas,
   faFileLines, faFolderTree, faMicrophone, faRss, faSearch, faServer, faVolumeHigh,
   faCircleDot, faDiagramProject, faCircleNodes, faPencil, faRoute
} from "@fortawesome/free-solid-svg-icons";
import {
   faConnectdevelop, faFacebookMessenger, faGoogle, faGoogleDrive, faInstagram,
   faJira, faMeta, faNotion, faSlack, faTrello, faWhatsapp, faWordpress
} from "@fortawesome/free-brands-svg-icons";
import { faFile as faFileRegular, faFolderOpen as faFolderOpenRegular } from "@fortawesome/free-regular-svg-icons";
import {
   PageContainer, TopBar, TopBarLeft, TopBarRight, TopBarBtn, LogoMark,
   BodyLayout, StepsSidebar, SidebarTitle, StepItem, StepNumber, StepLabel,
   MainContent, SectionHeader, SectionTitle, SectionSubtitle,
   HeroBanner, HeroContent, HeroText, HeroVisual, HeroBadge,
   FormSection, FormLabel, FormHint, InlineValidationMessage, FormInput, FormTextarea, FormSelect,
   FormRow, FormGroup,
   MoodGrid, MoodCard,
   PromptAssistantBox, SmallBtn,
   SimulatorContainer, SimulatorControls, SimulatorResults, MetricsGrid, MetricCard,
   ConversationList, ConversationItem, RatingBadge,
   FeedbackList, FeedbackItem, FeedbackActions,
   ExportContainer, ExportButton, ExportSummary,
   Divider, Tag, LoadingDots, EmptyState,
   OutputFormatGrid, OutputFormatCard, TemperatureVisual, CardsGrid, FeatureCard,
   SavedAgentsPanel, PanelHeader, SavedAgentsList, SavedAgentCard,
   ExportModeSelector, ExportModeCard, WorkflowPickerList, WorkflowPickerItem,
   ImprovementPreview,
   LandingWrap, LandingHeader, LandingTitleBlock, LandingCreateBtn,
   LandingStats, LandingStatCard, LandingSearchBar, LandingFilters,
   LandingFilterBtn, LandingGrid, LandingCard, LandingEmpty,
   AgentOrbWrap,
   CardActionModalOverlay, CardActionModalContainer, CardActionModalHeader,
   CardActionModalTitle, CardActionModalCloseBtn, CardActionModalFormGroup,
   CardActionModalActions, CardActionModalButton
} from "./style";

/* ======================== TYPES ======================== */
type MaxIterationsValue = number | 'auto';

interface AgentConfig {
   name: string;
   model: string;
   temperature: number;
   mood: string;
   systemPrompt: string;
   userPromptSeed: string;
   outputFormat: string;
   memory: string;
   reasoning: string;
   maxIterations: MaxIterationsValue;
   customInstructions: string;
   markdownConfigRaw: string;
   markdownConfig: MarkdownAgentConfig | null;
   markdownConfigStatus: MarkdownConfigStatus;
   markdownConfigErrors: string[];
   markdownConfigWarnings: string[];
   markdownFileName: string;
   useToolController: boolean;
   strictRequiredToolEnforcement: boolean;
   toolPolicies: AgentToolPolicy[];
}

type MarkdownConfigStatus = 'empty' | 'valid' | 'invalid' | 'parsing';
type ToolPolicyMode = 'required' | 'auto' | 'disabled';
type ToolPolicyAggregateMode = ToolPolicyMode | 'mixed';
type OperationPolicySource = 'operation' | 'tool' | 'none';

interface AgentToolPolicy {
   operationId: string;
   mode: ToolPolicyMode;
}

interface StudioToolCatalogOperation {
   operationId: string;
   operationName: string;
   operationDescription: string;
   inputSchema: Array<{
      name: string;
      type: string;
      required: boolean;
      description: string;
   }>;
}

interface StudioToolCatalogTool {
   toolName: string;
   displayName?: string;
   toolDescription: string;
   operations: StudioToolCatalogOperation[];
}

interface ToolGenerationFeedback {
   type: 'success' | 'error' | 'info';
   message: string;
   timestamp: number;
}

interface MarkdownAgentTool {
   name: string;
   description: string;
   when: string;
   inputs: string[];
   outputs: string[];
   constraints: string[];
}

interface MarkdownAgentConfig {
   instructions: string[];
   tools: MarkdownAgentTool[];
   rules: string[];
   examples: string[];
   metadata?: {
      sectionOrder?: string[];
      unknownSections?: string[];
      source?: string;
      parsedAt?: string;
   };
}

interface SimulatedConversation {
   id: number;
   userMessage: string;
   agentResponse: string;
   rating: 'good' | 'bad' | 'neutral';
   status: 'success' | 'error';
   tokens: number;
   latency: number;
   expanded: boolean;
}

interface SimulationMetrics {
   accuracy: number;
   hallucinationRate: number;
   avgTokens: number;
   avgLatency: number;
   totalConversations: number;
   goodRatings: number;
   badRatings: number;
}

interface FeedbackEntry {
   id: number;
   originalResponse: string;
   improvedPrompt: string;
   userMessage: string;
   agentResponse: string;
   rating: 'bad';
   timestamp: string;
   isGenerating?: boolean;
   applied?: boolean;
   dismissed?: boolean;
}

interface SavedAgent {
   _id: string;
   name: string;
   model: string;
   temperature: number;
   mood: string;
   systemPrompt: string;
   userPromptSeed: string;
   outputFormat: string;
   memory: string;
   reasoning: string;
   maxIterations: MaxIterationsValue;
   customInstructions: string;
   markdownConfigRaw?: string;
   markdownConfig?: MarkdownAgentConfig | null;
   markdownConfigStatus?: MarkdownConfigStatus;
   markdownConfigErrors?: string[];
   markdownConfigWarnings?: string[];
   markdownFileName?: string;
   useToolController?: boolean;
   strictRequiredToolEnforcement?: boolean;
   toolPolicies?: AgentToolPolicy[];
   createdAt: string;
   updatedAt: string;
}

interface WorkflowItem {
   _id: string;
   name: string;
   description: string;
   nodeCount: number;
   createdAt: string;
}

interface SelectedToolCardItem {
   toolName: string;
   mode: ToolPolicyAggregateMode;
   selectedOperations: number;
   totalOperations: number;
}

/* ======================== CONSTANTS ======================== */
const STEP_IDS = {
   WELCOME: 1,
   CONFIGURATION: 2,
   PERSONALITY_DESIGNER: 3,
   TOOLS_CONFIGURATION: 4,
   PROMPT_STUDIO: 5,
   CONVERSATION_SIMULATOR: 6,
   SELF_IMPROVEMENT: 7,
   EXPORT_TO_WORKFLOW: 8,
} as const;

const STEPS = [
   { id: STEP_IDS.WELCOME, label: 'Welcome', icon: faStar },
   { id: STEP_IDS.CONFIGURATION, label: 'Configuration', icon: faGear },
   { id: STEP_IDS.PERSONALITY_DESIGNER, label: 'Personality Designer', icon: faWandMagicSparkles },
   { id: STEP_IDS.TOOLS_CONFIGURATION, label: 'Tools Configuration', icon: faLayerGroup },
   { id: STEP_IDS.PROMPT_STUDIO, label: 'Prompt Studio', icon: faLightbulb },
   { id: STEP_IDS.CONVERSATION_SIMULATOR, label: 'Conversation Simulator', icon: faFlask },
   { id: STEP_IDS.SELF_IMPROVEMENT, label: 'Self-Improvement', icon: faBrain },
   { id: STEP_IDS.EXPORT_TO_WORKFLOW, label: 'Export to Workflow', icon: faFileExport },
];

const AGENT_NAME_REQUIRED_MESSAGE = 'Please enter an agent name before saving';
const UNSAVED_AGENT_LEAVE_MESSAGE = 'This agent is not saved. Are you sure you want to leave?';
const AUTO_MAX_ITERATIONS_MODE = 'auto';
const MIN_ITERATION_LIMIT = 3;
const MAX_ITERATION_LIMIT = 60;

const parseIterationNumber = (value: unknown): number | undefined => {
   if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return Math.min(MAX_ITERATION_LIMIT, Math.max(MIN_ITERATION_LIMIT, Math.round(value)));
   }

   const raw = String(value ?? '').trim();
   if (!raw) return undefined;

   const parsed = Number.parseInt(raw, 10);
   if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
   return Math.min(MAX_ITERATION_LIMIT, Math.max(MIN_ITERATION_LIMIT, parsed));
};

const normalizeReasoningAndMaxIterations = (
   reasoningInput: unknown,
   maxIterationsInput: unknown
): { reasoning: string; maxIterations: MaxIterationsValue } => {
   const reasoningNumeric = parseIterationNumber(reasoningInput);
   const maxIterationsNumeric = parseIterationNumber(maxIterationsInput);

   if (maxIterationsNumeric !== undefined) {
      return {
         reasoning: String(maxIterationsNumeric),
         maxIterations: maxIterationsNumeric,
      };
   }

   if (String(maxIterationsInput ?? '').trim().toLowerCase() === AUTO_MAX_ITERATIONS_MODE) {
      return {
         reasoning: AUTO_MAX_ITERATIONS_MODE,
         maxIterations: AUTO_MAX_ITERATIONS_MODE,
      };
   }

   if (reasoningNumeric !== undefined) {
      return {
         reasoning: String(reasoningNumeric),
         maxIterations: reasoningNumeric,
      };
   }

   if (String(reasoningInput ?? '').trim().toLowerCase() === AUTO_MAX_ITERATIONS_MODE) {
      return {
         reasoning: AUTO_MAX_ITERATIONS_MODE,
         maxIterations: AUTO_MAX_ITERATIONS_MODE,
      };
   }

   return {
      reasoning: AUTO_MAX_ITERATIONS_MODE,
      maxIterations: AUTO_MAX_ITERATIONS_MODE,
   };
};

const formatMaxIterationsForCard = (value: MaxIterationsValue): string => {
   if (value === AUTO_MAX_ITERATIONS_MODE) return 'Auto';
   const parsed = parseIterationNumber(value);
   return parsed !== undefined ? String(parsed) : 'Auto';
};

const createDefaultAgentConfig = (): AgentConfig => ({
   name: '',
   model: 'gemini-2.5-flash',
   temperature: 0.3,
   mood: '',
   systemPrompt: '',
   userPromptSeed: '',
   outputFormat: 'text',
   memory: 'simple-memory',
   reasoning: AUTO_MAX_ITERATIONS_MODE,
   maxIterations: AUTO_MAX_ITERATIONS_MODE,
   customInstructions: '',
   markdownConfigRaw: '',
   markdownConfig: null,
   markdownConfigStatus: 'empty',
   markdownConfigErrors: [],
   markdownConfigWarnings: [],
   markdownFileName: '',
   useToolController: true,
   strictRequiredToolEnforcement: true,
   toolPolicies: [],
});

const MODELS = [
   { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', provider: 'Google' },
   { id: 'gemini-2.5-flash-lite', name: 'Gemini 2.5 Flash Lite', provider: 'Google' },
   { id: 'gemini-2.0-flash', name: 'Gemini 2.0 Flash', provider: 'Google' },
   { id: 'gemini-2.0-flash-lite', name: 'Gemini 2.0 Flash Lite', provider: 'Google' },
   { id: 'gpt-4o-mini', name: 'GPT-4o Mini', provider: 'OpenAI' },
   { id: 'gpt-5.4', name: 'GPT-5.4', provider: 'OpenAI' },
   { id: 'gpt-5.4-mini', name: 'GPT-5.4 Mini', provider: 'OpenAI' },
   { id: 'gpt-5.4-nano', name: 'GPT-5.4 Nano', provider: 'OpenAI' },
   { id: 'gemini-3.1-pro-preview', name: 'Gemini 3.1 Pro Preview', provider: 'Google' },
   { id: 'gemini-3.1-flash-lite-preview', name: 'Gemini 3.1 Flash Lite Preview', provider: 'Google' },
   { id: 'gemini-3-flash-preview', name: 'Gemini 3 Flash Preview', provider: 'Google' },
];

const MOODS = [
   { id: 'strict', label: 'Strict', icon: '🎯', color: '#ef4444', desc: 'Precise, formal, no small talk' },
   { id: 'friendly', label: 'Friendly', icon: '😊', color: '#10b981', desc: 'Warm, conversational, helpful' },
   { id: 'advisor', label: 'Advisor', icon: '🧠', color: '#8b5cf6', desc: 'Thoughtful, strategic, insightful' },
   { id: 'salesperson', label: 'Salesperson', icon: '💼', color: '#f59e0b', desc: 'Persuasive, enthusiastic, solution-focused' },
   { id: 'teacher', label: 'Teacher', icon: '📚', color: '#3b82f6', desc: 'Patient, explanatory, step-by-step' },
   { id: 'creative', label: 'Creative', icon: '🎨', color: '#ec4899', desc: 'Imaginative, unconventional, inspiring' },
];

const OUTPUT_FORMATS = [
   { id: 'text', label: 'Plain Text', icon: '📄' },
   { id: 'json', label: 'JSON', icon: '{ }' },
   { id: 'markdown', label: 'Markdown', icon: '📝' },
   { id: 'html', label: 'HTML', icon: '🌐' },
   { id: 'list', label: 'Bullet List', icon: '📋' },
   { id: 'structured', label: 'Structured', icon: '🏗️' },
];

const MOOD_PROMPT_MAP: Record<string, string> = {
   strict: 'You are a precise and professional assistant. Respond with clarity and accuracy. Avoid unnecessary conversation. Be concise, factual, and direct. Do not use emojis or casual language.',
   friendly: 'You are a warm and friendly assistant who genuinely cares about helping the user. Use a conversational tone, be approachable, and make the user feel comfortable. You can use light humor when appropriate.',
   advisor: 'You are a thoughtful and strategic advisor. Analyze questions deeply, provide well-reasoned insights, and consider multiple perspectives. Offer actionable recommendations backed by logic.',
   salesperson: 'You are an enthusiastic and persuasive assistant focused on providing solutions. Highlight benefits, address concerns proactively, and guide the user toward the best outcome. Be energetic and solution-oriented.',
   teacher: 'You are a patient and knowledgeable teacher. Break down complex topics into simple, easy-to-understand explanations. Use examples, analogies, and step-by-step guidance to ensure understanding.',
   creative: 'You are an imaginative and creative assistant. Think outside the box, offer unique perspectives, and inspire the user with innovative ideas. Be expressive and encourage creative thinking.',
};

const TOOL_POLICY_MODE_ORDER: ToolPolicyMode[] = ['required', 'auto', 'disabled'];
const TOOL_POLICY_TOOL_PREFIX = 'tool:';

const toToolPolicyId = (toolName: string) => `${TOOL_POLICY_TOOL_PREFIX}${toolName}`;

const TOOL_POLICY_MODE_META: Record<ToolPolicyMode, {
   label: string;
   compactLabel: string;
   accent: string;
   background: string;
   border: string;
}> = {
   required: {
      label: 'Required',
      compactLabel: 'REQ',
      accent: '#b42318',
      background: '#fff1f2',
      border: '#fecdd3',
   },
   auto: {
      label: 'Auto',
      compactLabel: 'AUTO',
      accent: '#1d4ed8',
      background: '#eff6ff',
      border: '#bfdbfe',
   },
   disabled: {
      label: 'Disabled',
      compactLabel: 'OFF',
      accent: '#b45309',
      background: '#fff7ed',
      border: '#fed7aa',
   },
};

const TOOL_VISUAL_META: Record<string, { image?: string; accent: string; icon: any }> = {
   airtable: { image: '/images/airtable.png', accent: '#000000', icon: faDatabase },
   azureStorage: { image: '/images/azure.svg', accent: '#3b82f6', icon: faCloud },
   bigQuery: { accent: '#498bfc', icon: faSearch },
   clickup: { image: '/images/clickup.svg', accent: '#ff4ca0', icon: faCheck },
   code: { accent: '#ff9922', icon: faCode },
   customAPI: { accent: '#336699', icon: faConnectdevelop },
   docSearch: { accent: '#eab308', icon: faFolderTree },
   editFields: { accent: '#898fff', icon: faPencil },
   erpnext: { image: '/images/erpnext.png', accent: '#0089ff', icon: faCircleDot },
   fileUploader: { accent: '#06b6d4', icon: faCloudArrowUp },
   flowQueue: { accent: '#ff66cc', icon: faDiagramProject },
   gmail: { accent: '#ef4444', icon: faEnvelope },
   googleCalendar: { accent: '#4d8ffa', icon: faCalendar },
   googleDrive: { accent: '#22c55e', icon: faGoogleDrive },
   googleGemini: { accent: '#5393fd', icon: faGoogle },
   instaSendMessage: { accent: '#d62976', icon: faInstagram },
   jira: { accent: '#0a61f1', icon: faJira },
   knowledge: { accent: '#6b7280', icon: faFolderOpenRegular },
   llama: { accent: '#0182f0', icon: faMeta },
   messengerSendMessage: { accent: '#08aaff', icon: faFacebookMessenger },
   notion: { accent: '#080808', icon: faNotion },
   ocr: { accent: '#06b6d4', icon: faFileLines },
   odoo: { accent: '#a54c8d', icon: faCircleDot },
   openai: { image: '/images/openai.svg', accent: '#17a683', icon: faRobot },
   outlook: { image: '/images/outlook.svg', accent: '#3b82f6', icon: faEnvelope },
   pdfChunker: { accent: '#e52429', icon: faCopy },
   queryDatabase: { accent: '#06b6d4', icon: faServer },
   routing: { accent: '#b453e6', icon: faRoute },
   rss: { accent: '#f89d3e', icon: faRss },
   slack: { accent: '#963498', icon: faSlack },
   speechToText: { accent: '#4caf50', icon: faMicrophone },
   spreadsheet: { accent: '#22c55e', icon: faFileRegular },
   sql: { accent: '#0a6fa0', icon: faDatabase },
   supabase: { image: '/images/supabase.png', accent: '#16a34a', icon: faDatabase },
   textToSpeech: { accent: '#ff6b35', icon: faVolumeHigh },
   trello: { accent: '#2d88ff', icon: faTrello },
   twilioSendSms: { image: '/images/Twilio.png', accent: '#cf272d', icon: faComments },
   urlSearch: { accent: '#a855f7', icon: faEarthAmericas },
   videoVision: { accent: '#4d88ff', icon: faCircleNodes },
   webSearch: { accent: '#2eb907', icon: faSearch },
   whatsappSendMessage: { accent: '#25d366', icon: faWhatsapp },
   wordPress: { accent: '#08789d', icon: faWordpress },
};

const formatToolDisplayName = (toolName: string) => {
   return String(toolName || '')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getToolVisualMeta = (toolName: string) => {
   const visual = TOOL_VISUAL_META[toolName] || TOOL_VISUAL_META[String(toolName || '').toLowerCase()];
   if (visual) return visual;
   return { accent: '#1d4ed8', icon: faBolt };
};

const SAMPLE_QUESTIONS = [
   "What are your business hours?",
   "How can I track my order?",
   "I want to cancel my subscription",
   "Do you offer refunds?",
   "What payment methods do you accept?",
   "Can you help me with a technical issue?",
   "What are the pricing plans?",
   "How do I contact support?",
   "Is there a free trial available?",
   "Can I upgrade my plan?",
   "I forgot my password",
   "Where can I find the documentation?",
   "Do you have an API?",
   "What integrations do you support?",
   "How do I export my data?",
   "Tell me about your security features",
   "What's new in the latest update?",
   "Can I use this for my team?",
   "How do I set up automation?",
   "What languages do you support?",
   "How do I delete my account?",
   "Can I get a demo of the product?",
   "What is your refund policy?",
   "How do I add team members?",
   "Is my data encrypted?",
   "Do you offer enterprise plans?",
   "How do I connect my CRM?",
   "What happens when my trial ends?",
   "Can I import data from another tool?",
   "How do I set up notifications?",
   "What are the system requirements?",
   "Do you have a mobile app?",
   "How do I generate reports?",
   "Can I customize the dashboard?",
   "What is the uptime guarantee?",
   "How do I reset my API key?",
   "Can I white-label the product?",
   "What analytics do you provide?",
   "How do I schedule a task?",
   "Do you support single sign-on?",
   "How do I manage user permissions?",
   "Can I use webhooks?",
   "What is your data retention policy?",
   "How do I troubleshoot connection issues?",
   "Do you offer training or onboarding?",
   "Can I export reports as PDF?",
   "How do I set up two-factor authentication?",
   "What browsers are supported?",
   "How do I contact the billing department?",
   "Can I downgrade my plan later?",
];

/* ======================== AGENT ORB ======================== */
const hashStr = (s: string): number => {
   let h = 0;
   for (let i = 0; i < s.length; i++) { h = (Math.imul(31, h) + s.charCodeAt(i)) | 0; }
   return Math.abs(h);
};

const seededRand = (seed: number): number => {
   const x = Math.sin(seed + 1) * 43758.5453123;
   return x - Math.floor(x);
};

const MOOD_HUE: Record<string, number> = {
   strict: 0, friendly: 140, advisor: 260, salesperson: 30, teacher: 210, creative: 300,
};

const AgentOrb: React.FC<{ name: string; model: string; mood: string; size?: number }> = ({
   name, model, mood, size = 70,
}) => {
   const hash = hashStr((name || 'AI') + (model || ''));
   const h1 = hash % 360;
   const h2 = (h1 + 130 + (hash % 50)) % 360;
   const h3 = MOOD_HUE[(mood || '').toLowerCase()] ?? ((hash * 7 + 240) % 360);
   const rawName = (name || 'AI Agent').replace(/[^a-zA-Z ]/g, '').trim();
   const initials = rawName.split(/\s+/).map((w: string) => w[0]).join('').slice(0, 2).toUpperCase() || 'AI';
   const uid = `o${hash % 99991}`;
   const cx = size / 2, cy = size / 2, r = size * 0.33;

   const nodes = Array.from({ length: 6 }, (_, i) => {
      const angle = seededRand(hash + i * 113) * Math.PI * 2;
      const dist = r * 1.62 + seededRand(hash + i * 79) * r * 0.26;
      return {
         x: cx + Math.cos(angle) * dist,
         y: cy + Math.sin(angle) * dist,
         r: 1.5 + seededRand(hash + i * 53) * 2,
         op: 0.45 + seededRand(hash + i * 37) * 0.45,
         hue: (h1 + i * 45) % 360,
      };
   });

   const lines: [number, number][] = [[0, 1], [1, 3], [3, 5], [5, 2], [2, 4], [4, 0]];

   return (
      <AgentOrbWrap>
         <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} style={{ overflow: 'visible' }}>
            <defs>
               <radialGradient id={`g1${uid}`} cx="35%" cy="30%" r="70%">
                  <stop offset="0%" stopColor={`hsl(${h1},90%,82%)`} />
                  <stop offset="55%" stopColor={`hsl(${h2},75%,55%)`} />
                  <stop offset="100%" stopColor={`hsl(${h3},80%,30%)`} />
               </radialGradient>
               <radialGradient id={`g2${uid}`} cx="28%" cy="22%" r="48%">
                  <stop offset="0%" stopColor="rgba(255,255,255,0.68)" />
                  <stop offset="100%" stopColor="rgba(255,255,255,0)" />
               </radialGradient>
               <filter id={`gw${uid}`} x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation={size * 0.07} result="blur" />
                  <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
               </filter>
            </defs>
            {lines.map(([a, b], i) => {
               const na = nodes[a], nb = nodes[b];
               if (!na || !nb) return null;
               return (
                  <line key={i}
                     x1={na.x} y1={na.y} x2={nb.x} y2={nb.y}
                     stroke={`hsl(${h2},60%,75%)`} strokeWidth="0.7" opacity="0.28" strokeDasharray="3 2"
                  />
               );
            })}
            {nodes.map((n, i) => (
               <circle key={i} cx={n.x} cy={n.y} r={n.r}
                  fill={`hsl(${n.hue},80%,78%)`} opacity={n.op} className="orb-node"
                  style={{ animationDelay: `${i * 0.35}s` }}
               />
            ))}
            <circle cx={cx} cy={cy} r={r + 10} fill="none"
               stroke={`hsl(${h1},70%,65%)`} strokeWidth="1" className="orb-halo"
            />
            <circle cx={cx} cy={cy} r={r} fill={`url(#g1${uid})`} filter={`url(#gw${uid})`} />
            <circle cx={cx} cy={cy} r={r} fill={`url(#g2${uid})`} />
            <text
               x={cx} y={cy + (initials.length === 1 ? 6 : 5.5)}
               textAnchor="middle" fill="white" fontWeight="800"
               fontSize={initials.length === 1 ? size * 0.28 : size * 0.22}
               fontFamily="system-ui,-apple-system,sans-serif" opacity="0.95"
            >{initials}</text>
         </svg>
      </AgentOrbWrap>
   );
};

/* ======================== COMPONENT ======================== */
export function AIAgentStudio() {
   const [viewMode, setViewMode] = React.useState<'landing' | 'studio'>('landing');
   const [activeStep, setActiveStep] = React.useState<number>(STEP_IDS.WELCOME);
   const [completedSteps, setCompletedSteps] = React.useState<number[]>([]);
   const [searchQuery, setSearchQuery] = React.useState('');
   const [agentFilter, setAgentFilter] = React.useState<'all' | 'active' | 'recent'>('all');

   // Agent configuration state
   const [config, setConfig] = React.useState<AgentConfig>(createDefaultAgentConfig());

   // Prompt assistant state
   const [isAssistantExpanding, setIsAssistantExpanding] = React.useState(false);
   const [expandedPrompt, setExpandedPrompt] = React.useState('');
   const [isPromptAccepted, setIsPromptAccepted] = React.useState(false);

   // Tool controller state
   const [toolCatalog, setToolCatalog] = React.useState<StudioToolCatalogTool[]>([]);
   const [isLoadingToolCatalog, setIsLoadingToolCatalog] = React.useState(false);
   const [toolCatalogError, setToolCatalogError] = React.useState('');
   const [toolSearch, setToolSearch] = React.useState('');
   const [isGeneratingToolMarkdown, setIsGeneratingToolMarkdown] = React.useState(false);
   const [toolGenerationWarnings, setToolGenerationWarnings] = React.useState<string[]>([]);
   const [toolGenerationFeedback, setToolGenerationFeedback] = React.useState<ToolGenerationFeedback | null>(null);
   const [toolMarkdownPolicySignature, setToolMarkdownPolicySignature] = React.useState<string | null>(null);
   const [expandedTools, setExpandedTools] = React.useState<Record<string, boolean>>({});
   const [catalogFocusToolName, setCatalogFocusToolName] = React.useState('');

   // Simulator state
   const [simCount, setSimCount] = React.useState(10);
   const [isSimulating, setIsSimulating] = React.useState(false);
   const [simConversations, setSimConversations] = React.useState<SimulatedConversation[]>([]);
   const [simMetrics, setSimMetrics] = React.useState<SimulationMetrics | null>(null);

   // Self-improvement state
   const [feedbackEntries, setFeedbackEntries] = React.useState<FeedbackEntry[]>([]);
   const [isImproving, setIsImproving] = React.useState(false);
   const [improvedPromptPreview, setImprovedPromptPreview] = React.useState<string>('');
   const [showPreview, setShowPreview] = React.useState(false);

   // Export state
   const [isExporting, setIsExporting] = React.useState(false);
   const [exportComplete, setExportComplete] = React.useState(false);
   const [exportMode, setExportMode] = React.useState<'new' | 'existing' | null>(null);
   const [workflows, setWorkflows] = React.useState<WorkflowItem[]>([]);
   const [selectedWorkflowId, setSelectedWorkflowId] = React.useState('');
   const [isLoadingWorkflows, setIsLoadingWorkflows] = React.useState(false);
   const [exportWarnings, setExportWarnings] = React.useState<string[]>([]);

   // Saved agents state
   const [savedAgents, setSavedAgents] = React.useState<SavedAgent[]>([]);
   const [currentAgentId, setCurrentAgentId] = React.useState<string | null>(null);
   const [lastSavedConfig, setLastSavedConfig] = React.useState<AgentConfig>(createDefaultAgentConfig());
   const [isSaving, setIsSaving] = React.useState(false);
   const [nameValidationError, setNameValidationError] = React.useState('');
   const [isUnsavedLeaveModalOpen, setIsUnsavedLeaveModalOpen] = React.useState(false);
   const [isToolMarkdownLeaveModalOpen, setIsToolMarkdownLeaveModalOpen] = React.useState(false);
   const [pendingToolMarkdownStep, setPendingToolMarkdownStep] = React.useState<number | null>(null);
   const [isLoadingAgents, setIsLoadingAgents] = React.useState(false);
   const [activeCardMenuId, setActiveCardMenuId] = React.useState<string | null>(null);
   const [selectedCardAgent, setSelectedCardAgent] = React.useState<SavedAgent | null>(null);
   const [renameDraft, setRenameDraft] = React.useState('');
   const [isRenameModalOpen, setIsRenameModalOpen] = React.useState(false);
   const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
   const saveInFlightRef = React.useRef<Promise<boolean> | null>(null);
   const exportInFlightRef = React.useRef(false);
   const agentNameInputRef = React.useRef<HTMLInputElement | null>(null);
   const markdownUploadInputRef = React.useRef<HTMLInputElement | null>(null);
   const pendingLeaveActionRef = React.useRef<'landing' | null>(null);
   const toolCardRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
   const previousActiveStepRef = React.useRef<number>(STEP_IDS.WELCOME);
   const toolMarkdownNavigationGuardBypassRef = React.useRef(false);

   React.useEffect(() => {
      if (!catalogFocusToolName) return;
      const timeout = window.setTimeout(() => {
         setCatalogFocusToolName('');
      }, 2200);

      return () => window.clearTimeout(timeout);
   }, [catalogFocusToolName]);

   React.useEffect(() => {
      if (config.name.trim() && nameValidationError) {
         setNameValidationError('');
      }
   }, [config.name, nameValidationError]);

   const normalizeMarkdownConfigForCompare = React.useCallback((markdownConfig: MarkdownAgentConfig | null) => {
      if (!markdownConfig) return null;
      return {
         ...markdownConfig,
         metadata: {
            ...(markdownConfig.metadata || {}),
            parsedAt: '',
         },
      };
   }, []);

   const normalizeToolPoliciesForCompare = React.useCallback((toolPolicies: AgentToolPolicy[] | undefined): AgentToolPolicy[] => {
      const list = Array.isArray(toolPolicies) ? toolPolicies : [];
      return list
         .filter((policy) => Boolean(policy?.operationId))
         .map((policy): AgentToolPolicy => {
            const mode: ToolPolicyMode =
               policy.mode === 'required' || policy.mode === 'disabled'
                  ? policy.mode
                  : 'auto';

            return {
               operationId: String(policy.operationId).trim(),
               mode,
            };
         })
         .sort((left, right) => left.operationId.localeCompare(right.operationId));
   }, []);

   const buildToolPolicySignature = React.useCallback((toolPolicies: AgentToolPolicy[] | undefined): string => {
      return JSON.stringify(normalizeToolPoliciesForCompare(toolPolicies));
   }, [normalizeToolPoliciesForCompare]);

   const currentToolPolicySignature = React.useMemo(() => {
      return buildToolPolicySignature(config.toolPolicies);
   }, [buildToolPolicySignature, config.toolPolicies]);

   const normalizedToolPolicies = React.useMemo(() => {
      return normalizeToolPoliciesForCompare(config.toolPolicies);
   }, [config.toolPolicies, normalizeToolPoliciesForCompare]);

   const hasToolPolicyEntries = normalizedToolPolicies.length > 0;

   const hasOutdatedToolMarkdown = React.useMemo(() => {
      if (!config.markdownConfigRaw.trim() || !toolMarkdownPolicySignature) return false;
      return currentToolPolicySignature !== toolMarkdownPolicySignature;
   }, [config.markdownConfigRaw, currentToolPolicySignature, toolMarkdownPolicySignature]);

   const isToolMarkdownMissingForPolicies = React.useMemo(() => {
      if (!hasToolPolicyEntries) return false;
      if (!config.markdownConfigRaw.trim()) return true;
      return !toolMarkdownPolicySignature;
   }, [config.markdownConfigRaw, hasToolPolicyEntries, toolMarkdownPolicySignature]);

   const toolMarkdownSyncWarnings = React.useMemo(() => {
      const warnings: string[] = [];

      if (isToolMarkdownMissingForPolicies) {
         warnings.push('Tool policies are configured, but markdown was not generated from policies yet.');
      }

      if (hasOutdatedToolMarkdown) {
         warnings.push(
            hasToolPolicyEntries
               ? 'Tools or operations were changed after the last markdown generation.'
               : 'Tool policies were removed after the last markdown generation.'
         );
      }

      return warnings;
   }, [hasOutdatedToolMarkdown, hasToolPolicyEntries, isToolMarkdownMissingForPolicies]);

   React.useEffect(() => {
      if (config.markdownConfigRaw.trim()) return;
      if (toolMarkdownPolicySignature !== null) {
         setToolMarkdownPolicySignature(null);
      }
   }, [config.markdownConfigRaw, toolMarkdownPolicySignature]);

   React.useEffect(() => {
      const previousStep = previousActiveStepRef.current;
      const leftToolsConfiguration = previousStep === STEP_IDS.TOOLS_CONFIGURATION && activeStep !== STEP_IDS.TOOLS_CONFIGURATION;

      if (
         leftToolsConfiguration &&
         toolMarkdownSyncWarnings.length > 0 &&
         !toolMarkdownNavigationGuardBypassRef.current
      ) {
         setPendingToolMarkdownStep(activeStep);
         setIsToolMarkdownLeaveModalOpen(true);
         toolMarkdownNavigationGuardBypassRef.current = true;
         setActiveStep(STEP_IDS.TOOLS_CONFIGURATION);
         return;
      }

      toolMarkdownNavigationGuardBypassRef.current = false;
      previousActiveStepRef.current = activeStep;
   }, [activeStep, toolMarkdownSyncWarnings.length]);

   const serializeConfig = React.useCallback((agentConfig: AgentConfig) => {
      const normalizedIterations = normalizeReasoningAndMaxIterations(
         agentConfig.reasoning,
         agentConfig.maxIterations
      );

      return JSON.stringify({
      name: agentConfig.name,
      model: agentConfig.model,
      temperature: agentConfig.temperature,
      mood: agentConfig.mood,
      systemPrompt: agentConfig.systemPrompt,
      userPromptSeed: agentConfig.userPromptSeed,
      outputFormat: agentConfig.outputFormat,
      memory: agentConfig.memory,
      reasoning: normalizedIterations.reasoning,
      maxIterations: normalizedIterations.maxIterations,
      customInstructions: agentConfig.customInstructions,
      markdownConfigRaw: agentConfig.markdownConfigRaw,
      markdownConfigStatus: agentConfig.markdownConfigStatus,
      markdownConfigErrors: agentConfig.markdownConfigErrors,
      markdownConfigWarnings: agentConfig.markdownConfigWarnings,
      markdownFileName: agentConfig.markdownFileName,
      useToolController: agentConfig.useToolController,
      strictRequiredToolEnforcement: agentConfig.strictRequiredToolEnforcement,
      toolPolicies: normalizeToolPoliciesForCompare(agentConfig.toolPolicies),
      markdownConfig: normalizeMarkdownConfigForCompare(agentConfig.markdownConfig),
   });
   }, [normalizeMarkdownConfigForCompare, normalizeToolPoliciesForCompare]);

   const hasUnsavedChanges = React.useMemo(() => {
      if (viewMode !== 'studio') return false;
      return serializeConfig(config) !== serializeConfig(lastSavedConfig);
   }, [viewMode, config, lastSavedConfig, serializeConfig]);

   const returnToLanding = React.useCallback(() => {
      setViewMode('landing');
      setActiveCardMenuId(null);
   }, []);

   const requestLeaveToLanding = React.useCallback(() => {
      if (!hasUnsavedChanges) {
         returnToLanding();
         return;
      }
      pendingLeaveActionRef.current = 'landing';
      setIsUnsavedLeaveModalOpen(true);
   }, [hasUnsavedChanges, returnToLanding]);

   const cancelUnsavedLeave = React.useCallback(() => {
      pendingLeaveActionRef.current = null;
      setIsUnsavedLeaveModalOpen(false);
   }, []);

   const confirmUnsavedLeave = React.useCallback(() => {
      const pendingAction = pendingLeaveActionRef.current;
      pendingLeaveActionRef.current = null;
      setIsUnsavedLeaveModalOpen(false);

      if (pendingAction === 'landing') {
         setConfig(lastSavedConfig);
         setNameValidationError('');
         returnToLanding();
      }
   }, [lastSavedConfig, returnToLanding]);

   const closeToolMarkdownLeaveModal = React.useCallback(() => {
      setPendingToolMarkdownStep(null);
      setIsToolMarkdownLeaveModalOpen(false);
   }, []);

   const continueWithoutToolMarkdownSync = React.useCallback(() => {
      const targetStep = pendingToolMarkdownStep;
      closeToolMarkdownLeaveModal();

      if (typeof targetStep === 'number' && targetStep !== STEP_IDS.TOOLS_CONFIGURATION) {
         toolMarkdownNavigationGuardBypassRef.current = true;
         setActiveStep(targetStep);
      }
   }, [closeToolMarkdownLeaveModal, pendingToolMarkdownStep]);

   React.useEffect(() => {
      const handleParentMessage = (event: MessageEvent) => {
         if (!event.data || typeof event.data !== 'object') return;
         if ((event.data as { type?: string }).type === 'AI_AGENT_STUDIO_NAVIGATE_LANDING') {
            requestLeaveToLanding();
         }
      };

      window.addEventListener('message', handleParentMessage);
      return () => window.removeEventListener('message', handleParentMessage);
   }, [requestLeaveToLanding]);

   React.useEffect(() => {
      if (window.parent && window.parent !== window) {
         window.parent.postMessage({ type: 'AI_AGENT_STUDIO_DIRTY_STATE', dirty: hasUnsavedChanges }, '*');
      }
   }, [hasUnsavedChanges]);

   React.useEffect(() => {
      return () => {
         if (window.parent && window.parent !== window) {
            window.parent.postMessage({ type: 'AI_AGENT_STUDIO_DIRTY_STATE', dirty: false }, '*');
         }
      };
   }, []);

   React.useEffect(() => {
      const handleBeforeUnload = (event: BeforeUnloadEvent) => {
         if (!hasUnsavedChanges) return;
         event.preventDefault();
         event.returnValue = '';
      };

      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
   }, [hasUnsavedChanges]);

   /* ================ HELPERS ================ */
   const updateConfig = (updates: Partial<AgentConfig>) => {
      setConfig(prev => ({ ...prev, ...updates }));
   };

   const markStepCompleted = (step: number) => {
      setCompletedSteps(prev => prev.includes(step) ? prev : [...prev, step]);
   };

   const isStepCompleted = (step: number) => completedSteps.includes(step);

   const goToStep = (step: number) => {
      // Mark current step as completed if moving forward
      if (step > activeStep) markStepCompleted(activeStep);
      setActiveStep(step);
   };

   const nextStep = () => {
      markStepCompleted(activeStep);
      if (activeStep < STEPS.length) setActiveStep(activeStep + 1);
   };

   /* ================ BUILD SYSTEM PROMPT ================ */
   const buildMarkdownPromptAppendix = React.useCallback(() => {
      if (config.markdownConfigStatus !== 'valid' || !config.markdownConfig) return '';

      const lines: string[] = [];
      lines.push('Uploaded Markdown Configuration:');

      if (config.markdownConfig.instructions.length) {
         lines.push('Instructions:');
         config.markdownConfig.instructions.forEach((instruction) => lines.push(`- ${instruction}`));
      }

      if (config.markdownConfig.rules.length) {
         lines.push('Rules:');
         config.markdownConfig.rules.forEach((rule) => lines.push(`- ${rule}`));
      }

      if (config.markdownConfig.tools.length) {
         lines.push('Tool Guidance:');
         config.markdownConfig.tools.forEach((tool) => {
            lines.push(`- ${tool.name}: ${tool.description || 'No description provided.'}`);
         });
      }

      if (config.markdownConfig.examples.length) {
         lines.push('Examples:');
         config.markdownConfig.examples.slice(0, 5).forEach((example, index) => {
            lines.push(`${index + 1}. ${example}`);
         });
      }

      return lines.join('\n');
   }, [config.markdownConfigStatus, config.markdownConfig]);

   const buildSystemPrompt = (): string => {
      let prompt = '';
      if (config.mood && MOOD_PROMPT_MAP[config.mood]) {
         prompt = MOOD_PROMPT_MAP[config.mood];
      }
      if (config.systemPrompt) {
         prompt = prompt ? `${prompt}\n\n${config.systemPrompt}` : config.systemPrompt;
      }
      if (config.customInstructions) {
         prompt += `\n\nAdditional instructions: ${config.customInstructions}`;
      }
      if (config.outputFormat && config.outputFormat !== 'text') {
         prompt += `\n\nAlways format your response as ${config.outputFormat.toUpperCase()}.`;
      }

      const markdownAppendix = buildMarkdownPromptAppendix();
      if (markdownAppendix) {
         prompt += `\n\n${markdownAppendix}`;
      }

      return prompt;
   };

   /* ================ PROMPT ASSISTANT ================ */
   const getAuthHeaders = React.useCallback(() => {
      const cookies = new Cookies();
      const token = cookies.get('token');
      return { headers: { 'x-auth-token': token } };
   }, []);

   const applyMarkdownConfigValidation = React.useCallback((payload: {
      isValid?: boolean;
      errors?: string[];
      warnings?: string[];
      config?: MarkdownAgentConfig | null;
      markdownFileName?: string;
      markdownRaw: string;
   }) => {
      const hasMarkdown = Boolean(payload.markdownRaw.trim());
      if (!hasMarkdown) {
         updateConfig({
            markdownConfigRaw: '',
            markdownConfig: null,
            markdownConfigStatus: 'empty',
            markdownConfigErrors: [],
            markdownConfigWarnings: [],
            markdownFileName: '',
         });
         setToolMarkdownPolicySignature(null);
         return;
      }

      updateConfig({
         markdownConfigRaw: payload.markdownRaw,
         markdownConfig: payload.config || null,
         markdownConfigStatus: payload.isValid ? 'valid' : 'invalid',
         markdownConfigErrors: payload.errors || [],
         markdownConfigWarnings: payload.warnings || [],
         markdownFileName: payload.markdownFileName || config.markdownFileName,
      });
   }, [config.markdownFileName]);

   const validateMarkdownConfig = React.useCallback(async (markdownRaw: string, fileName?: string) => {
      const markdown = String(markdownRaw || '');
      if (!markdown.trim()) {
         applyMarkdownConfigValidation({ markdownRaw: '' });
         return;
      }

      updateConfig({
         markdownConfigRaw: markdown,
         markdownConfigStatus: 'parsing',
         markdownConfigErrors: [],
         markdownConfigWarnings: [],
         markdownFileName: fileName || config.markdownFileName,
      });

      try {
         const { data } = await axios.post(
            `${webURL}/agents/studio/parse-markdown-config`,
            {
               markdown,
               fileName: fileName || config.markdownFileName,
               systemPrompt: config.systemPrompt || '',
               userPrompt: config.customInstructions || '',
            },
            getAuthHeaders()
         );

         applyMarkdownConfigValidation({
            markdownRaw: markdown,
            isValid: data?.isValid,
            errors: data?.errors,
            warnings: data?.warnings,
            config: data?.config,
            markdownFileName: data?.markdownFileName || fileName || config.markdownFileName,
         });
      } catch (err: any) {
         const msg = err?.response?.data?.error || err?.message || 'Failed to validate markdown config';
         updateConfig({
            markdownConfigRaw: markdown,
            markdownConfig: null,
            markdownConfigStatus: 'invalid',
            markdownConfigErrors: [msg],
            markdownConfigWarnings: [],
            markdownFileName: fileName || config.markdownFileName,
         });
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [applyMarkdownConfigValidation, config.markdownFileName, getAuthHeaders]);

   const onMarkdownFilePick = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const extension = file.name.toLowerCase();
      if (!extension.endsWith('.md')) {
         updateConfig({
            markdownConfigStatus: 'invalid',
            markdownConfigErrors: ['Only Markdown (.md) files are supported.'],
            markdownConfigWarnings: [],
         });
         event.target.value = '';
         return;
      }

      try {
         const markdown = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result || ''));
            reader.onerror = () => reject(new Error('Failed to read markdown file'));
            reader.readAsText(file);
         });

         await validateMarkdownConfig(markdown, file.name);
      } catch (err: any) {
         const msg = err?.message || 'Failed to read markdown file';
         updateConfig({
            markdownConfigStatus: 'invalid',
            markdownConfigErrors: [msg],
            markdownConfigWarnings: [],
         });
      } finally {
         event.target.value = '';
      }
   };

   const removeMarkdownConfig = () => {
      applyMarkdownConfigValidation({ markdownRaw: '' });
   };

   const loadToolCatalog = React.useCallback(async () => {
      setIsLoadingToolCatalog(true);
      setToolCatalogError('');

      try {
         const { data } = await axios.get(`${webURL}/agents/studio/tools-catalog`, getAuthHeaders());
         setToolCatalog(Array.isArray(data?.tools) ? data.tools : []);
      } catch (err: any) {
         const msg = err?.response?.data?.error || err?.message || 'Failed to load tools catalog';
         setToolCatalogError(msg);
         setToolCatalog([]);
      } finally {
         setIsLoadingToolCatalog(false);
      }
   }, [getAuthHeaders]);

   React.useEffect(() => {
      if (viewMode !== 'studio') return;
      if (toolCatalog.length > 0 || isLoadingToolCatalog) return;
      void loadToolCatalog();
   }, [viewMode, toolCatalog.length, isLoadingToolCatalog, loadToolCatalog]);

   React.useEffect(() => {
      setExpandedTools((prev) => {
         const knownToolNames = new Set(toolCatalog.map((tool) => tool.toolName));
         const next: Record<string, boolean> = {};
         let changed = false;

         for (const [toolName, expanded] of Object.entries(prev)) {
            if (knownToolNames.has(toolName)) {
               next[toolName] = expanded;
            } else {
               changed = true;
            }
         }

         return changed ? next : prev;
      });
   }, [toolCatalog]);

   const toolCatalogByName = React.useMemo(() => {
      const map = new Map<string, StudioToolCatalogTool>();
      for (const tool of toolCatalog) {
         map.set(tool.toolName, tool);
      }
      return map;
   }, [toolCatalog]);

   const toolDisplayNameByName = React.useMemo(() => {
      const map = new Map<string, string>();
      for (const tool of toolCatalog) {
         const displayName = String(tool.displayName || '').trim();
         map.set(tool.toolName, displayName || formatToolDisplayName(tool.toolName));
      }
      return map;
   }, [toolCatalog]);

   const getToolDisplayName = React.useCallback((toolName: string) => {
      return toolDisplayNameByName.get(toolName) || formatToolDisplayName(toolName);
   }, [toolDisplayNameByName]);

   const operationToToolNameMap = React.useMemo(() => {
      const map: Record<string, string> = {};
      for (const tool of toolCatalog) {
         for (const operation of tool.operations) {
            map[operation.operationId] = tool.toolName;
         }
      }
      return map;
   }, [toolCatalog]);

   const knownPolicyIds = React.useMemo(() => {
      const set = new Set<string>();
      for (const tool of toolCatalog) {
         set.add(toToolPolicyId(tool.toolName));
         for (const operation of tool.operations) {
            set.add(operation.operationId);
         }
      }
      return set;
   }, [toolCatalog]);

   const orphanPolicies = React.useMemo(() => {
      return normalizeToolPoliciesForCompare(config.toolPolicies).filter((policy) => !knownPolicyIds.has(policy.operationId));
   }, [config.toolPolicies, knownPolicyIds, normalizeToolPoliciesForCompare]);

   const explicitPolicyModeMap = React.useMemo(() => {
      const map = new Map<string, ToolPolicyMode>();
      for (const policy of config.toolPolicies) {
         const normalizedMode: ToolPolicyMode =
            policy.mode === 'required' || policy.mode === 'disabled'
               ? policy.mode
               : 'auto';
         map.set(String(policy.operationId || '').trim(), normalizedMode);
      }
      return map;
   }, [config.toolPolicies]);

   const resolveToolNameForOperationId = React.useCallback((operationId: string) => {
      if (operationToToolNameMap[operationId]) return operationToToolNameMap[operationId];
      const separatorIndex = operationId.indexOf('_');
      if (separatorIndex <= 0) return '';
      return operationId.slice(0, separatorIndex);
   }, [operationToToolNameMap]);

   const getExplicitPolicyMode = React.useCallback((policyId: string): ToolPolicyMode | null => {
      const mode = explicitPolicyModeMap.get(policyId);
      return mode || null;
   }, [explicitPolicyModeMap]);

   const getToolPolicyMode = React.useCallback((toolName: string): ToolPolicyMode | null => {
      if (!toolName) return null;
      return getExplicitPolicyMode(toToolPolicyId(toolName));
   }, [getExplicitPolicyMode]);

   const getOperationPolicyState = React.useCallback((operationId: string): { mode: ToolPolicyMode; source: OperationPolicySource } => {
      const explicitOperationMode = getExplicitPolicyMode(operationId);
      if (explicitOperationMode) {
         return { mode: explicitOperationMode, source: 'operation' };
      }

      const toolName = resolveToolNameForOperationId(operationId);
      const toolMode = getToolPolicyMode(toolName);
      if (toolMode) {
         return { mode: toolMode, source: 'tool' };
      }

      return { mode: 'disabled', source: 'none' };
   }, [getExplicitPolicyMode, getToolPolicyMode, resolveToolNameForOperationId]);

   const getPolicyMode = React.useCallback((operationId: string): ToolPolicyMode => {
      return getOperationPolicyState(operationId).mode;
   }, [getOperationPolicyState]);

   const getToolAggregateMode = React.useCallback((toolName: string): ToolPolicyAggregateMode => {
      const tool = toolCatalogByName.get(toolName);
      if (!tool || tool.operations.length === 0) return 'disabled';

      const allModes = tool.operations.map((operation) => getPolicyMode(operation.operationId));
      const firstMode = allModes[0];
      if (allModes.every((mode) => mode === firstMode)) return firstMode;
      return 'mixed';
   }, [getPolicyMode, toolCatalogByName]);

   const getToolModeSummary = React.useCallback((toolName: string) => {
      const tool = toolCatalogByName.get(toolName);
      const summary = {
         effectiveRequired: 0,
         effectiveAuto: 0,
         effectiveDisabled: 0,
         explicitRequired: 0,
         explicitAuto: 0,
         explicitDisabled: 0,
         explicitTotal: 0,
         inheritedTotal: 0,
      };
      if (!tool) return summary;

      for (const operation of tool.operations) {
         const policyState = getOperationPolicyState(operation.operationId);

         if (policyState.mode === 'required') summary.effectiveRequired += 1;
         else if (policyState.mode === 'auto') summary.effectiveAuto += 1;
         else summary.effectiveDisabled += 1;

         if (policyState.source === 'operation') {
            summary.explicitTotal += 1;
            if (policyState.mode === 'required') summary.explicitRequired += 1;
            else if (policyState.mode === 'auto') summary.explicitAuto += 1;
            else summary.explicitDisabled += 1;
         } else if (policyState.source === 'tool') {
            summary.inheritedTotal += 1;
         }
      }

      return summary;
   }, [getOperationPolicyState, toolCatalogByName]);

   const setOperationPolicyMode = React.useCallback((operationId: string, mode: ToolPolicyMode) => {
      setConfig((prev) => {
         const nextPolicies = prev.toolPolicies.filter((policy) => policy.operationId !== operationId);
         nextPolicies.push({ operationId, mode });

         return {
            ...prev,
            toolPolicies: normalizeToolPoliciesForCompare(nextPolicies),
         };
      });
   }, [normalizeToolPoliciesForCompare]);

   const setToolPolicyModeForTool = React.useCallback((toolName: string, mode: ToolPolicyMode) => {
      const tool = toolCatalogByName.get(toolName);
      if (!tool) return;
      const toolPolicyId = toToolPolicyId(toolName);

      setConfig((prev) => {
         const nextPolicies = prev.toolPolicies.filter(
            (policy) => policy.operationId !== toolPolicyId && !tool.operations.some((operation) => operation.operationId === policy.operationId)
         );
         nextPolicies.push({ operationId: toolPolicyId, mode });

         return {
            ...prev,
            toolPolicies: normalizeToolPoliciesForCompare(nextPolicies),
         };
      });
   }, [normalizeToolPoliciesForCompare, toolCatalogByName]);

   const toggleToolExpanded = React.useCallback((toolName: string) => {
      setExpandedTools((prev) => ({
         ...prev,
         [toolName]: !prev[toolName],
      }));
   }, []);

   const clearAllToolPolicies = React.useCallback(() => {
      setToolGenerationWarnings([]);
      setToolGenerationFeedback({
         type: 'info',
         message: 'Tool policies and generated markdown were cleared.',
         timestamp: Date.now(),
      });
      applyMarkdownConfigValidation({ markdownRaw: '' });
      updateConfig({ toolPolicies: [] });
   }, [applyMarkdownConfigValidation]);

   const clearToolPoliciesForTool = React.useCallback((toolName: string) => {
      if (!toolName) return;

      setConfig((prev) => {
         const tool = toolCatalogByName.get(toolName);
         const operationIds = new Set(tool?.operations.map((operation) => operation.operationId) || []);
         const nextPolicies = prev.toolPolicies.filter((policy) => {
            const policyId = String(policy.operationId || '').trim();
            if (!policyId) return false;
            if (policyId === toToolPolicyId(toolName)) return false;
            if (operationIds.has(policyId)) return false;

            const inferredToolName = policyId.startsWith(TOOL_POLICY_TOOL_PREFIX)
               ? policyId.slice(TOOL_POLICY_TOOL_PREFIX.length)
               : resolveToolNameForOperationId(policyId);

            return inferredToolName !== toolName;
         });

         return {
            ...prev,
            toolPolicies: normalizeToolPoliciesForCompare(nextPolicies),
         };
      });
   }, [normalizeToolPoliciesForCompare, resolveToolNameForOperationId, toolCatalogByName]);

   const focusToolInCatalog = React.useCallback((toolName: string) => {
      if (!toolName) return;

      setExpandedTools((prev) => ({
         ...prev,
         [toolName]: true,
      }));

      setToolSearch((prev) => {
         const query = prev.trim().toLowerCase();
         if (!query) return prev;
         if (toolName.toLowerCase().includes(query)) return prev;
         return toolName;
      });

      setCatalogFocusToolName(toolName);

      window.setTimeout(() => {
         const target = toolCardRefs.current[toolName];
         if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
         }
      }, 120);
   }, []);

   const filteredToolCatalog = React.useMemo(() => {
      const query = toolSearch.trim().toLowerCase();
      if (!query) return toolCatalog;

      return toolCatalog
         .map((tool) => {
            const matchedOperations = tool.operations.filter((operation) => {
               const haystack = [
                  operation.operationId,
                  operation.operationName,
                  operation.operationDescription,
               ].join(' ').toLowerCase();
               return haystack.includes(query);
            });

            const toolMatches = [tool.toolName, tool.displayName || '', tool.toolDescription].join(' ').toLowerCase().includes(query);
            if (toolMatches) return tool;
            if (matchedOperations.length) return { ...tool, operations: matchedOperations };
            return null;
         })
         .filter(Boolean) as StudioToolCatalogTool[];
   }, [toolCatalog, toolSearch]);

   const expandVisibleTools = React.useCallback(() => {
      setExpandedTools((prev) => {
         const next = { ...prev };
         for (const tool of filteredToolCatalog) {
            next[tool.toolName] = true;
         }
         return next;
      });
   }, [filteredToolCatalog]);

   const collapseVisibleTools = React.useCallback(() => {
      setExpandedTools((prev) => {
         const next = { ...prev };
         for (const tool of filteredToolCatalog) {
            next[tool.toolName] = false;
         }
         return next;
      });
   }, [filteredToolCatalog]);

   const toolPolicySummary = React.useMemo(() => {
      const allOperationIds = toolCatalog.flatMap((tool) => tool.operations.map((operation) => operation.operationId));
      let required = 0;
      let auto = 0;
      let disabled = 0;

      for (const operationId of allOperationIds) {
         const mode = getPolicyMode(operationId);
         if (mode === 'required') required += 1;
         else if (mode === 'auto') auto += 1;
         else disabled += 1;
      }

      const selected = required + auto;

      return {
         required,
         auto,
         disabled,
         selected,
         total: config.toolPolicies.length,
         totalOperations: allOperationIds.length,
      };
   }, [config.toolPolicies.length, getPolicyMode, toolCatalog]);

   const selectedToolsCardItems = React.useMemo((): SelectedToolCardItem[] => {
      const normalizedPolicies = normalizeToolPoliciesForCompare(config.toolPolicies);
      const fallbackModesByTool = new Map<string, ToolPolicyMode[]>();

      for (const policy of normalizedPolicies) {
         const policyId = String(policy.operationId || '').trim();
         if (!policyId) continue;

         const toolName = policyId.startsWith(TOOL_POLICY_TOOL_PREFIX)
            ? policyId.slice(TOOL_POLICY_TOOL_PREFIX.length)
            : resolveToolNameForOperationId(policyId);

         if (!toolName) continue;

         const modes = fallbackModesByTool.get(toolName) || [];
         modes.push(policy.mode);
         fallbackModesByTool.set(toolName, modes);
      }

      const items: SelectedToolCardItem[] = [];

      for (const [toolName, modes] of fallbackModesByTool.entries()) {
         const tool = toolCatalogByName.get(toolName);
         const requiredCount = modes.filter((mode) => mode === 'required').length;
         const autoCount = modes.filter((mode) => mode === 'auto').length;
         const disabledCount = modes.filter((mode) => mode === 'disabled').length;
         const fallbackSelectedCount = requiredCount + autoCount;
         const hasEnabledPolicy = fallbackSelectedCount > 0;

         let mode: ToolPolicyAggregateMode;
         if (tool) {
            mode = getToolAggregateMode(toolName);
         } else if (requiredCount > 0 && autoCount > 0) {
            mode = 'mixed';
         } else if (requiredCount > 0) {
            mode = disabledCount > 0 ? 'mixed' : 'required';
         } else if (autoCount > 0) {
            mode = disabledCount > 0 ? 'mixed' : 'auto';
         } else {
            mode = 'disabled';
         }

         const modeSummary = tool ? getToolModeSummary(toolName) : null;
         const selectedOperations = modeSummary
            ? modeSummary.effectiveRequired + modeSummary.effectiveAuto
            : fallbackSelectedCount;
         const totalOperations = tool?.operations.length || Math.max(modes.length, 1);

         if (!hasEnabledPolicy && selectedOperations === 0 && mode === 'disabled') continue;

         items.push({
            toolName,
            mode,
            selectedOperations,
            totalOperations,
         });
      }

      return items.sort((left, right) => left.toolName.localeCompare(right.toolName));
   }, [
      config.toolPolicies,
      getToolAggregateMode,
      getToolModeSummary,
      normalizeToolPoliciesForCompare,
      resolveToolNameForOperationId,
      toolCatalogByName,
   ]);

   const selectedToolOpsCount = React.useMemo(() => {
      return selectedToolsCardItems.reduce((sum, item) => sum + item.selectedOperations, 0);
   }, [selectedToolsCardItems]);

   const preExportWarnings = React.useMemo(() => {
      const warnings: string[] = [];

      if (config.useToolController && config.toolPolicies.length > 0 && selectedToolsCardItems.length === 0) {
         warnings.push('Tool controller is enabled, but no selected tools resolve from your current policy set.');
      }

      if (!config.useToolController && config.toolPolicies.length > 0) {
         warnings.push('Tool policies are configured while Tool Controller is disabled. Runtime enforcement will remain off until you enable it.');
      }

      if (orphanPolicies.length > 0) {
         warnings.push(`${orphanPolicies.length} policy entr${orphanPolicies.length === 1 ? 'y' : 'ies'} reference tool/operation IDs not currently in the catalog.`);
      }

      return warnings;
   }, [config.toolPolicies.length, config.useToolController, orphanPolicies.length, selectedToolsCardItems.length]);

   const handleGenerateToolMarkdown = React.useCallback(async (): Promise<boolean> => {
      setIsGeneratingToolMarkdown(true);
      setToolGenerationWarnings([]);
      setToolGenerationFeedback(null);

      try {
         const { data } = await axios.post(
            `${webURL}/agents/studio/generate-tool-markdown`,
            {
               toolPolicies: config.toolPolicies,
               systemPrompt: config.systemPrompt || '',
               userPrompt: config.customInstructions || '',
            },
            getAuthHeaders()
         );

         const nextWarnings = Array.isArray(data?.warnings) ? data.warnings : [];
         const normalizedPolicies = normalizeToolPoliciesForCompare(Array.isArray(data?.normalizedPolicies) ? data.normalizedPolicies : config.toolPolicies);
         const nextFileName = config.markdownFileName || 'tool-policy.generated.md';

         updateConfig({
            toolPolicies: normalizedPolicies,
            markdownConfigRaw: String(data?.markdown || ''),
            markdownConfig: data?.config || null,
            markdownConfigStatus: data?.isValid ? 'valid' : 'invalid',
            markdownConfigErrors: Array.isArray(data?.errors) ? data.errors : [],
            markdownConfigWarnings: nextWarnings,
            markdownFileName: nextFileName,
         });
         setToolMarkdownPolicySignature(buildToolPolicySignature(normalizedPolicies));
         setToolGenerationWarnings(nextWarnings);

         const hasWarnings = nextWarnings.length > 0;
         setToolGenerationFeedback({
            type: hasWarnings ? 'info' : 'success',
            message: hasWarnings
               ? `Markdown generated with ${nextWarnings.length} warning(s) and synced to Prompt Studio.`
               : `Markdown generated successfully and synced to Prompt Studio as ${nextFileName}.`,
            timestamp: Date.now(),
         });
         return true;
      } catch (err: any) {
         const msg = err?.response?.data?.error || err?.message || 'Failed to generate tool markdown';
         setToolGenerationWarnings([msg]);
         setToolGenerationFeedback({
            type: 'error',
            message: msg,
            timestamp: Date.now(),
         });
         return false;
      } finally {
         setIsGeneratingToolMarkdown(false);
      }
   }, [buildToolPolicySignature, config.customInstructions, config.markdownFileName, config.systemPrompt, config.toolPolicies, getAuthHeaders, normalizeToolPoliciesForCompare]);

   const syncMarkdownAndContinueNavigation = React.useCallback(async () => {
      const targetStep = pendingToolMarkdownStep;
      const synced = await handleGenerateToolMarkdown();
      if (!synced) return;

      closeToolMarkdownLeaveModal();
      if (typeof targetStep === 'number' && targetStep !== STEP_IDS.TOOLS_CONFIGURATION) {
         toolMarkdownNavigationGuardBypassRef.current = true;
         setActiveStep(targetStep);
      }
   }, [closeToolMarkdownLeaveModal, handleGenerateToolMarkdown, pendingToolMarkdownStep]);

   /* ================ SAVED AGENTS ================ */
   const loadSavedAgents = React.useCallback(async () => {
      setIsLoadingAgents(true);
      try {
         const { data } = await axios.get(`${webURL}/agents/studio/agents`, getAuthHeaders());
         setSavedAgents(data.agents || []);
      } catch {
         // Silently fail — panel just shows empty
      } finally {
         setIsLoadingAgents(false);
      }
   }, [getAuthHeaders]);

   React.useEffect(() => {
      document.title = 'Dragify AI - AI Agent Studio';
      loadSavedAgents();
   }, [loadSavedAgents]);

   const buildSavePayload = () => {
      const normalizedIterations = normalizeReasoningAndMaxIterations(config.reasoning, config.maxIterations);

      return {
      name: config.name,
      model: config.model,
      temperature: config.temperature,
      mood: config.mood,
      systemPrompt: config.systemPrompt,
      userPromptSeed: config.userPromptSeed,
      outputFormat: config.outputFormat,
      memory: config.memory,
      reasoning: normalizedIterations.reasoning,
      maxIterations: normalizedIterations.maxIterations,
      customInstructions: config.customInstructions,
      markdownConfigRaw: config.markdownConfigRaw,
      markdownConfig: config.markdownConfig,
      markdownConfigStatus: config.markdownConfigStatus,
      markdownConfigErrors: config.markdownConfigErrors,
      markdownConfigWarnings: config.markdownConfigWarnings,
      markdownFileName: config.markdownFileName,
      useToolController: config.useToolController,
      strictRequiredToolEnforcement: config.strictRequiredToolEnforcement,
      toolPolicies: normalizeToolPoliciesForCompare(config.toolPolicies),
   };
   };

   const buildSavedAgentComparablePayload = (agent: SavedAgent) => {
      const normalizedIterations = normalizeReasoningAndMaxIterations(agent.reasoning, agent.maxIterations);

      return {
      name: agent.name,
      model: agent.model,
      temperature: agent.temperature,
      mood: agent.mood,
      systemPrompt: agent.systemPrompt,
      userPromptSeed: agent.userPromptSeed,
      outputFormat: agent.outputFormat,
      memory: agent.memory,
      reasoning: normalizedIterations.reasoning,
      maxIterations: normalizedIterations.maxIterations,
      customInstructions: agent.customInstructions,
      markdownConfigRaw: agent.markdownConfigRaw || '',
      markdownConfig: normalizeMarkdownConfigForCompare(agent.markdownConfig || null),
      markdownConfigStatus: agent.markdownConfigStatus || 'empty',
      markdownConfigErrors: agent.markdownConfigErrors || [],
      markdownConfigWarnings: agent.markdownConfigWarnings || [],
      markdownFileName: agent.markdownFileName || '',
      useToolController: agent.useToolController ?? false,
      strictRequiredToolEnforcement: agent.strictRequiredToolEnforcement ?? true,
      toolPolicies: normalizeToolPoliciesForCompare(agent.toolPolicies || []),
   };
   };

   const isPayloadEqual = (
      left: ReturnType<typeof buildSavePayload>,
      right: ReturnType<typeof buildSavePayload>
   ) => (
      left.name.trim() === right.name.trim() &&
      left.model === right.model &&
      left.temperature === right.temperature &&
      left.mood === right.mood &&
      left.systemPrompt === right.systemPrompt &&
      left.userPromptSeed === right.userPromptSeed &&
      left.outputFormat === right.outputFormat &&
      left.memory === right.memory &&
      left.reasoning === right.reasoning &&
      left.maxIterations === right.maxIterations &&
      left.customInstructions === right.customInstructions &&
      left.markdownConfigRaw === right.markdownConfigRaw &&
      left.markdownConfigStatus === right.markdownConfigStatus &&
      left.markdownFileName === right.markdownFileName &&
      left.useToolController === right.useToolController &&
      left.strictRequiredToolEnforcement === right.strictRequiredToolEnforcement &&
      JSON.stringify(left.markdownConfigErrors || []) === JSON.stringify(right.markdownConfigErrors || []) &&
      JSON.stringify(left.markdownConfigWarnings || []) === JSON.stringify(right.markdownConfigWarnings || []) &&
      JSON.stringify(normalizeToolPoliciesForCompare(left.toolPolicies || [])) ===
         JSON.stringify(normalizeToolPoliciesForCompare(right.toolPolicies || [])) &&
      JSON.stringify(normalizeMarkdownConfigForCompare(left.markdownConfig || null)) ===
         JSON.stringify(normalizeMarkdownConfigForCompare(right.markdownConfig || null))
   );

   const focusAgentNameInput = () => {
      if (!agentNameInputRef.current) return;
      agentNameInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      agentNameInputRef.current.focus();
   };

   const showNameRequiredValidation = () => {
      setNameValidationError(AGENT_NAME_REQUIRED_MESSAGE);
      if (activeStep === STEP_IDS.EXPORT_TO_WORKFLOW) return;

      if (activeStep !== STEP_IDS.CONFIGURATION) {
         setActiveStep(STEP_IDS.CONFIGURATION);
         setTimeout(() => focusAgentNameInput(), 80);
         return;
      }

      focusAgentNameInput();
   };

   const saveCurrentAgent = async (): Promise<boolean> => {
      if (!config.name.trim()) {
         showNameRequiredValidation();
         return false;
      }

      if (saveInFlightRef.current) {
         return saveInFlightRef.current;
      }

      const payload = buildSavePayload();
      if (currentAgentId) {
         const savedAgent = savedAgents.find(a => a._id === currentAgentId);
         if (savedAgent && isPayloadEqual(payload, buildSavedAgentComparablePayload(savedAgent))) {
            setNameValidationError('');
            setLastSavedConfig(payload);
            return true;
         }
      }

      const saveRequest = (async () => {
         setIsSaving(true);
         try {
            if (currentAgentId) {
               const { data } = await axios.put(
                  `${webURL}/agents/studio/agents/${currentAgentId}`,
                  payload,
                  getAuthHeaders()
               );
               setSavedAgents(prev => prev.map(a => a._id === currentAgentId ? data.agent : a));
            } else {
               const { data } = await axios.post(
                  `${webURL}/agents/studio/agents`,
                  payload,
                  getAuthHeaders()
               );
               setSavedAgents(prev => [data.agent, ...prev]);
               setCurrentAgentId(data.agent._id);
            }
            setNameValidationError('');
            setLastSavedConfig(payload);
            return true;
         } catch (err: any) {
            const msg = err?.response?.data?.error || err.message || 'Failed to save agent';
            alert(`Save error: ${msg}`);
            return false;
         } finally {
            setIsSaving(false);
         }
      })();

      saveInFlightRef.current = saveRequest.finally(() => {
         saveInFlightRef.current = null;
      });

      return saveInFlightRef.current;
   };

   const loadAgent = (agent: SavedAgent) => {
      const normalizedPolicies = normalizeToolPoliciesForCompare(agent.toolPolicies || []);
      const normalizedIterations = normalizeReasoningAndMaxIterations(agent.reasoning, agent.maxIterations);

      const loadedConfig: AgentConfig = {
         name: agent.name,
         model: agent.model,
         temperature: agent.temperature,
         mood: agent.mood,
         systemPrompt: agent.systemPrompt,
         userPromptSeed: agent.userPromptSeed,
         outputFormat: agent.outputFormat,
         memory: agent.memory,
         reasoning: normalizedIterations.reasoning,
         maxIterations: normalizedIterations.maxIterations,
         customInstructions: agent.customInstructions,
         markdownConfigRaw: agent.markdownConfigRaw || '',
         markdownConfig: agent.markdownConfig || null,
         markdownConfigStatus: agent.markdownConfigStatus || (agent.markdownConfigRaw ? 'invalid' : 'empty'),
         markdownConfigErrors: agent.markdownConfigErrors || [],
         markdownConfigWarnings: agent.markdownConfigWarnings || [],
         markdownFileName: agent.markdownFileName || '',
         useToolController: agent.useToolController ?? false,
         strictRequiredToolEnforcement: agent.strictRequiredToolEnforcement ?? true,
         toolPolicies: normalizedPolicies,
      };

      setConfig(loadedConfig);
      setLastSavedConfig(loadedConfig);
      setCurrentAgentId(agent._id);
      toolMarkdownNavigationGuardBypassRef.current = true;
      previousActiveStepRef.current = STEP_IDS.WELCOME;
      setActiveStep(STEP_IDS.WELCOME);
      setCompletedSteps([]);
      setSimConversations([]);
      setSimMetrics(null);
      setFeedbackEntries([]);
      setExportComplete(false);
      setExportMode(null);
      setExportWarnings([]);
      setExpandedPrompt('');
      setIsPromptAccepted(false);
      setNameValidationError('');
      setToolGenerationWarnings([]);
      setToolMarkdownPolicySignature((agent.markdownConfigRaw || '').trim() ? buildToolPolicySignature(normalizedPolicies) : null);
   };

   const deleteAgent = async (agentId: string) => {
      try {
         await axios.delete(`${webURL}/agents/studio/agents/${agentId}`, getAuthHeaders());
         setSavedAgents(prev => prev.filter(a => a._id !== agentId));
         if (currentAgentId === agentId) {
            setCurrentAgentId(null);
         }
      } catch (err: any) {
         const msg = err?.response?.data?.error || err.message || 'Failed to delete';
         alert(`Delete error: ${msg}`);
      }
   };

   const buildAgentPayload = (agent: SavedAgent, overrides: Partial<SavedAgent> = {}) => {
      const normalizedIterations = normalizeReasoningAndMaxIterations(
         overrides.reasoning ?? agent.reasoning,
         overrides.maxIterations ?? agent.maxIterations
      );

      return {
      name: (overrides.name ?? agent.name ?? '').trim(),
      model: overrides.model ?? agent.model,
      temperature: overrides.temperature ?? agent.temperature,
      mood: overrides.mood ?? agent.mood,
      systemPrompt: overrides.systemPrompt ?? agent.systemPrompt,
      userPromptSeed: overrides.userPromptSeed ?? agent.userPromptSeed,
      outputFormat: overrides.outputFormat ?? agent.outputFormat,
      memory: overrides.memory ?? agent.memory,
      reasoning: normalizedIterations.reasoning,
      maxIterations: normalizedIterations.maxIterations,
      customInstructions: overrides.customInstructions ?? agent.customInstructions,
      markdownConfigRaw: overrides.markdownConfigRaw ?? agent.markdownConfigRaw ?? '',
      markdownConfig: overrides.markdownConfig ?? agent.markdownConfig ?? null,
      markdownConfigStatus: overrides.markdownConfigStatus ?? agent.markdownConfigStatus ?? 'empty',
      markdownConfigErrors: overrides.markdownConfigErrors ?? agent.markdownConfigErrors ?? [],
      markdownConfigWarnings: overrides.markdownConfigWarnings ?? agent.markdownConfigWarnings ?? [],
      markdownFileName: overrides.markdownFileName ?? agent.markdownFileName ?? '',
      useToolController: overrides.useToolController ?? agent.useToolController ?? false,
      strictRequiredToolEnforcement: overrides.strictRequiredToolEnforcement ?? agent.strictRequiredToolEnforcement ?? true,
      toolPolicies: normalizeToolPoliciesForCompare(overrides.toolPolicies ?? agent.toolPolicies ?? []),
   };
   };

   const renameAgent = async (agent: SavedAgent, nextName: string) => {
      setActiveCardMenuId(null);
      if (!nextName || nextName === agent.name) return;

      try {
         const { data } = await axios.put(
            `${webURL}/agents/studio/agents/${agent._id}`,
            buildAgentPayload(agent, { name: nextName }),
            getAuthHeaders()
         );

         setSavedAgents(prev => prev.map(a => a._id === agent._id ? data.agent : a));
         if (currentAgentId === agent._id) {
            setConfig(prev => ({ ...prev, name: data.agent.name }));
         }
      } catch (err: any) {
         const msg = err?.response?.data?.error || err.message || 'Failed to rename agent';
         alert(`Rename error: ${msg}`);
      }
   };

   const openRenameModal = (agent: SavedAgent) => {
      setSelectedCardAgent(agent);
      setRenameDraft(agent.name || '');
      setIsRenameModalOpen(true);
      setActiveCardMenuId(null);
   };

   const openDeleteModal = (agent: SavedAgent) => {
      setSelectedCardAgent(agent);
      setIsDeleteModalOpen(true);
      setActiveCardMenuId(null);
   };

   const closeCardActionModals = () => {
      setIsRenameModalOpen(false);
      setIsDeleteModalOpen(false);
      setSelectedCardAgent(null);
      setRenameDraft('');
   };

   const handleRenameSubmit = async () => {
      if (!selectedCardAgent) return;
      await renameAgent(selectedCardAgent, renameDraft.trim());
      closeCardActionModals();
   };

   const handleDeleteSubmit = async () => {
      if (!selectedCardAgent) return;
      await deleteAgent(selectedCardAgent._id);
      closeCardActionModals();
   };

   const duplicateAgent = async (agent: SavedAgent) => {
      setActiveCardMenuId(null);

      let copyIndex = 1;
      let copyName = `${agent.name} (Copy)`;
      const existingNames = new Set(savedAgents.map(a => a.name));
      while (existingNames.has(copyName)) {
         copyIndex += 1;
         copyName = `${agent.name} (Copy ${copyIndex})`;
      }

      try {
         const { data } = await axios.post(
            `${webURL}/agents/studio/agents`,
            buildAgentPayload(agent, { name: copyName }),
            getAuthHeaders()
         );

         setSavedAgents(prev => [data.agent, ...prev]);
      } catch (err: any) {
         const msg = err?.response?.data?.error || err.message || 'Failed to duplicate agent';
         alert(`Duplicate error: ${msg}`);
      }
   };

   const startNewAgent = () => {
      const defaultConfig = createDefaultAgentConfig();
      setCurrentAgentId(null);
      setConfig(defaultConfig);
      setLastSavedConfig(defaultConfig);
      toolMarkdownNavigationGuardBypassRef.current = true;
      previousActiveStepRef.current = STEP_IDS.WELCOME;
      setActiveStep(STEP_IDS.WELCOME);
      setCompletedSteps([]);
      setSimConversations([]);
      setSimMetrics(null);
      setFeedbackEntries([]);
      setExportComplete(false);
      setExportMode(null);
      setExportWarnings([]);
      setExpandedPrompt('');
      setIsPromptAccepted(false);
      setNameValidationError('');
      setToolGenerationWarnings([]);
      setToolMarkdownPolicySignature(null);
   };

   const openStudioForNewAgent = () => {
      startNewAgent();
      setViewMode('studio');
   };

   const openStudioForAgent = (agent: SavedAgent) => {
      loadAgent(agent);
      setViewMode('studio');
   };

   const activeAgentCount = React.useMemo(() => {
      const now = Date.now();
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      return savedAgents.filter(agent => now - new Date(agent.updatedAt).getTime() <= sevenDays).length;
   }, [savedAgents]);

   const recentAgentCount = React.useMemo(() => {
      const now = Date.now();
      const twoDays = 2 * 24 * 60 * 60 * 1000;
      return savedAgents.filter(agent => now - new Date(agent.updatedAt).getTime() <= twoDays).length;
   }, [savedAgents]);

   const filteredAgents = React.useMemo(() => {
      const now = Date.now();
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      const twoDays = 2 * 24 * 60 * 60 * 1000;
      return [...savedAgents]
         .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
         .filter(agent => {
            const query = searchQuery.trim().toLowerCase();
            const matchesSearch = !query ||
               agent.name.toLowerCase().includes(query) ||
               agent.model.toLowerCase().includes(query) ||
               agent.mood.toLowerCase().includes(query);

            const updatedAt = new Date(agent.updatedAt).getTime();
            const matchesFilter = agentFilter === 'all'
               ? true
               : agentFilter === 'active'
                  ? now - updatedAt <= sevenDays
                  : now - updatedAt <= twoDays;

            return matchesSearch && matchesFilter;
         });
   }, [savedAgents, searchQuery, agentFilter]);

   /* ================ WORKFLOW LOADING ================ */
   const loadWorkflows = async () => {
      setIsLoadingWorkflows(true);
      try {
         const { data } = await axios.get(`${webURL}/agents/studio/workflows`, getAuthHeaders());
         setWorkflows(data.workflows || []);
      } catch {
         setWorkflows([]);
      } finally {
         setIsLoadingWorkflows(false);
      }
   };

   const handleExpandPrompt = async () => {
      if (!config.userPromptSeed.trim()) return;
      setIsAssistantExpanding(true);
      setIsPromptAccepted(false);
      setExpandedPrompt('');

      try {
         const { data } = await axios.post(`${webURL}/agents/studio/expand-prompt`, {
            seed: config.userPromptSeed.trim(),
            mood: config.mood || undefined,
            model: config.model,
         }, getAuthHeaders());

         setExpandedPrompt(data.expandedPrompt);
      } catch (err: any) {
         const msg = err?.response?.data?.error || err.message || 'Failed to expand prompt';
         setExpandedPrompt(`[Error] ${msg}. Please try again.`);
      } finally {
         setIsAssistantExpanding(false);
      }
   };

   const acceptExpandedPrompt = () => {
      updateConfig({ systemPrompt: expandedPrompt });
      setIsPromptAccepted(true);
   };

   /* ================ CONVERSATION SIMULATOR ================ */
   const runSimulation = async () => {
      const prompt = buildSystemPrompt();
      if (!prompt.trim()) {
         alert('Please configure a system prompt first (Step 2 or 3).');
         return;
      }

      setIsSimulating(true);
      setSimConversations([]);
      setSimMetrics(null);

      const shuffled = [...SAMPLE_QUESTIONS].sort(() => Math.random() - 0.5);
      const questions = shuffled.slice(0, Math.min(simCount, SAMPLE_QUESTIONS.length));

      try {
         const { data } = await axios.post(`${webURL}/agents/studio/simulate`, {
            systemPrompt: prompt,
            model: config.model,
            temperature: config.temperature,
            questions,
            mood: config.mood || undefined,
         }, getAuthHeaders());

         const conversations: SimulatedConversation[] = (data.conversations || []).map((c: any, i: number) => ({
            id: i + 1,
            userMessage: c.userMessage,
            agentResponse: c.agentResponse,
            rating: c.status === 'error' ? 'bad' as const : 'neutral' as const,
            status: c.status === 'error' ? 'error' : 'success',
            tokens: c.tokens || 0,
            latency: c.latency || 0,
            expanded: false,
         }));

         setSimConversations(conversations);

         const metrics = data.metrics || {};
         const goodCount = conversations.filter(c => c.rating === 'good').length;
         const badCount = conversations.filter(c => c.rating === 'bad').length;

         setSimMetrics({
            accuracy: metrics.successfulConversations
               ? Math.round((metrics.successfulConversations / metrics.totalConversations) * 100)
               : 0,
            hallucinationRate: metrics.failedConversations
               ? Math.round((metrics.failedConversations / metrics.totalConversations) * 100)
               : 0,
            avgTokens: metrics.avgTokens || 0,
            avgLatency: metrics.avgLatency || 0,
            totalConversations: metrics.totalConversations || conversations.length,
            goodRatings: goodCount,
            badRatings: badCount,
         });
      } catch (err: any) {
         const msg = err?.response?.data?.error || err.message || 'Simulation failed';
         alert(`Simulation error: ${msg}`);
      } finally {
         setIsSimulating(false);
      }
   };

   const toggleConversation = (id: number) => {
      setSimConversations(prev =>
         prev.map(c => c.id === id ? { ...c, expanded: !c.expanded } : c)
      );
   };

   const rateConversation = (id: number, rating: 'good' | 'bad') => {
      setSimConversations(prev => {
         const updated = prev.map(c => c.id === id ? { ...c, rating } : c);

         // Recalculate metrics based on user ratings
         const goodCount = updated.filter(c => c.rating === 'good').length;
         const badCount = updated.filter(c => c.rating === 'bad').length;
         const successful = updated.filter(c => c.status === 'success');
         const totalTokens = successful.reduce((sum, c) => sum + c.tokens, 0);
         const totalLatency = successful.reduce((sum, c) => sum + c.latency, 0);

         setSimMetrics({
            accuracy: updated.length > 0 ? Math.round((goodCount / updated.length) * 100) : 0,
            hallucinationRate: updated.length > 0 ? Math.round((badCount / updated.length) * 100) : 0,
            avgTokens: successful.length > 0 ? Math.round(totalTokens / successful.length) : 0,
            avgLatency: successful.length > 0 ? Math.round(totalLatency / successful.length) : 0,
            totalConversations: updated.length,
            goodRatings: goodCount,
            badRatings: badCount,
         });

         return updated;
      });

      // If rated bad, add to feedback for self-improvement
      if (rating === 'bad') {
         const conv = simConversations.find(c => c.id === id);
         if (conv) {
            handleBadFeedback(conv);
         }
      }
   };

   /* ================ SELF-IMPROVEMENT ================ */
   const handleBadFeedback = async (conv: SimulatedConversation) => {
      const entryId = Date.now();
      const entry: FeedbackEntry = {
         id: entryId,
         originalResponse: conv.agentResponse.substring(0, 100) + '...',
         improvedPrompt: '',
         userMessage: conv.userMessage,
         agentResponse: conv.agentResponse,
         rating: 'bad',
         timestamp: new Date().toLocaleString(),
         isGenerating: true,
      };

      setFeedbackEntries(prev => [entry, ...prev]);

      // Call LLM to generate improvement suggestion for this specific bad response
      try {
         const { data } = await axios.post(`${webURL}/agents/studio/improve-prompt`, {
            // Use only the editable base prompt to avoid recursively re-ingesting runtime wrappers.
            currentPrompt: config.systemPrompt || 'You are a helpful assistant.',
            badResponses: [{ userMessage: conv.userMessage, agentResponse: conv.agentResponse }],
            model: config.model,
            mood: config.mood || undefined,
            customInstructions: config.customInstructions || undefined,
            outputFormat: config.outputFormat || undefined,
         }, getAuthHeaders());

         setFeedbackEntries(prev => prev.map(f =>
            f.id === entryId
               ? { ...f, improvedPrompt: data.improvedPrompt || 'No suggestion generated.', isGenerating: false }
               : f
         ));
      } catch {
         setFeedbackEntries(prev => prev.map(f =>
            f.id === entryId
               ? { ...f, improvedPrompt: 'Failed to generate improvement. Try again.', isGenerating: false }
               : f
         ));
      }
   };

   const applySingleImprovement = (entryId: number) => {
      const entry = feedbackEntries.find(f => f.id === entryId);
      if (!entry || !entry.improvedPrompt) return;
      updateConfig({ systemPrompt: entry.improvedPrompt });
      setFeedbackEntries(prev => prev.map(f =>
         f.id === entryId ? { ...f, applied: true } : f
      ));
   };

   const dismissImprovement = (entryId: number) => {
      setFeedbackEntries(prev => prev.map(f =>
         f.id === entryId ? { ...f, dismissed: true } : f
      ));
   };

   const undoDismiss = (entryId: number) => {
      setFeedbackEntries(prev => prev.map(f =>
         f.id === entryId ? { ...f, dismissed: false } : f
      ));
   };

   const applyAllImprovements = async () => {
      const actionableEntries = feedbackEntries.filter(f => !f.applied && !f.dismissed && f.improvedPrompt && !f.isGenerating);
      if (actionableEntries.length === 0) return;
      setIsImproving(true);
      setShowPreview(false);

      const badResponses = actionableEntries.map(f => ({
         userMessage: f.userMessage,
         agentResponse: f.agentResponse,
      }));

      try {
         const { data } = await axios.post(`${webURL}/agents/studio/improve-prompt`, {
            // Use only the editable base prompt to avoid prompt bloat over many iterations.
            currentPrompt: config.systemPrompt || 'You are a helpful assistant.',
            badResponses,
            model: config.model,
            mood: config.mood || undefined,
            customInstructions: config.customInstructions || undefined,
            outputFormat: config.outputFormat || undefined,
         }, getAuthHeaders());

         if (data.improvedPrompt) {
            setImprovedPromptPreview(data.improvedPrompt);
            setShowPreview(true);
         }
      } catch (err: any) {
         const msg = err?.response?.data?.error || err.message || 'Failed to improve prompt';
         alert(`Improvement error: ${msg}`);
      } finally {
         setIsImproving(false);
      }
   };

   const acceptImprovedPrompt = () => {
      updateConfig({ systemPrompt: improvedPromptPreview });
      setFeedbackEntries(prev => prev.map(f => ({ ...f, applied: true })));
      setShowPreview(false);
      setImprovedPromptPreview('');
      markStepCompleted(STEP_IDS.SELF_IMPROVEMENT);
   };

   const rejectImprovedPrompt = () => {
      setShowPreview(false);
      setImprovedPromptPreview('');
   };

   /* ================ EXPORT ================ */
   const [exportedProjectId, setExportedProjectId] = React.useState<string>('');

   const agentConfigPayload = () => {
      const normalizedIterations = normalizeReasoningAndMaxIterations(config.reasoning, config.maxIterations);

      // Build system prompt but WITHOUT the output format line (runtime adds outputFormat separately)
      let prompt = '';
      if (config.mood && MOOD_PROMPT_MAP[config.mood]) {
         prompt = MOOD_PROMPT_MAP[config.mood];
      }
      if (config.systemPrompt) {
         prompt = prompt ? `${prompt}\n\n${config.systemPrompt}` : config.systemPrompt;
      }
      if (config.customInstructions) {
         prompt += `\n\nAdditional instructions: ${config.customInstructions}`;
      }
      // Do NOT include outputFormat here — the AI Agent runtime adds it from the separate outputFormat field
      return {
         name: config.name || 'AI Studio Agent',
         model: config.model,
         temperature: config.temperature,
         memory: config.memory,
         reasoning: normalizedIterations.reasoning,
         maxIterations: normalizedIterations.maxIterations,
         systemPrompt: prompt,
         outputFormat: config.outputFormat !== 'text' ? config.outputFormat : '',
         mood: config.mood,
         customInstructions: config.customInstructions,
         userPromptSeed: config.userPromptSeed,
         markdownConfigRaw: config.markdownConfigRaw,
         markdownConfig: config.markdownConfig,
         markdownConfigStatus: config.markdownConfigStatus,
         markdownConfigErrors: config.markdownConfigErrors,
         markdownConfigWarnings: config.markdownConfigWarnings,
         markdownFileName: config.markdownFileName,
         useToolController: config.useToolController,
         strictRequiredToolEnforcement: config.strictRequiredToolEnforcement,
         toolPolicies: normalizeToolPoliciesForCompare(config.toolPolicies),
      };
   };

   const handleExportNew = async () => {
      setIsExporting(true);
      try {
         const { data } = await axios.post(`${webURL}/agents/studio/export`, {
            name: config.name || 'AI Studio Agent',
            agentConfig: agentConfigPayload(),
         }, getAuthHeaders());

         if (data.projectId) setExportedProjectId(data.projectId);
         const serverWarnings = Array.isArray(data?.warnings) ? data.warnings : [];
         setExportWarnings(Array.from(new Set([...preExportWarnings, ...serverWarnings].filter(Boolean))));
         setExportComplete(true);
      } catch (err: any) {
         const msg = err?.response?.data?.error || err.message || 'Export failed';
         alert(`Export error: ${msg}`);
      } finally {
         setIsExporting(false);
      }
   };

   const handleExportToExisting = async () => {
      if (!selectedWorkflowId) {
         alert('Please select a workflow first.');
         return;
      }
      setIsExporting(true);
      try {
         const { data } = await axios.post(`${webURL}/agents/studio/export-to-existing`, {
            projectId: selectedWorkflowId,
            name: config.name || 'AI Studio Agent',
            agentConfig: agentConfigPayload(),
         }, getAuthHeaders());

         if (data.projectId) setExportedProjectId(data.projectId);
         const serverWarnings = Array.isArray(data?.warnings) ? data.warnings : [];
         setExportWarnings(Array.from(new Set([...preExportWarnings, ...serverWarnings].filter(Boolean))));
         setExportComplete(true);
      } catch (err: any) {
         const msg = err?.response?.data?.error || err.message || 'Export failed';
         alert(`Export error: ${msg}`);
      } finally {
         setIsExporting(false);
      }
   };

   const handleExport = async () => {
      if (exportInFlightRef.current || isExporting) return;

      if (exportMode === 'existing' && !selectedWorkflowId) {
         alert('Please select a workflow first.');
         return;
      }

      if (!config.name.trim()) {
         showNameRequiredValidation();
         return;
      }

      setExportWarnings(preExportWarnings);

      exportInFlightRef.current = true;
      try {
         const saved = await saveCurrentAgent();
         if (!saved) return;

         if (exportMode === 'existing') {
            await handleExportToExisting();
         } else {
            await handleExportNew();
         }
      } finally {
         exportInFlightRef.current = false;
      }
   };

   /* ================ RENDERERS ================ */
   const renderStep1 = () => (
      <>
         <HeroBanner>
            <HeroContent>
               <HeroText>
                  <HeroBadge>
                     <FontAwesomeIcon icon={faStar} /> AI Agent Studio
                  </HeroBadge>
                  <h1>
                     Build Your <span>Intelligent Agent</span>
                  </h1>
                  <p>
                     Design, test, and deploy custom AI agents with personality, intelligence, and purpose.
                     Configure your agent&apos;s brain in minutes, not hours.
                  </p>
                  <SmallBtn $variant="primary" onClick={() => nextStep()}>
                     Get Started <FontAwesomeIcon icon={faArrowRight} />
                  </SmallBtn>
               </HeroText>
               <HeroVisual>
                  <div className="orbit-ring" />
                  <div className="orbit-ring-inner" />
                  <div className="center-icon">
                     <FontAwesomeIcon icon={faRobot} />
                  </div>
                  <div className="node-dot dot-1" />
                  <div className="node-dot dot-2" />
                  <div className="node-dot dot-3" />
                  <div className="node-dot dot-4" />
               </HeroVisual>
            </HeroContent>
         </HeroBanner>

         <CardsGrid>
            <FeatureCard $color="#3b82f6" onClick={() => setActiveStep(STEP_IDS.CONFIGURATION)}>
               <div className="card-icon"><FontAwesomeIcon icon={faGear} /></div>
               <h3>Configuration</h3>
               <p>Set your agent name, model, memory, temperature, and output settings.</p>
            </FeatureCard>
            <FeatureCard $color="#06b6d4" onClick={() => setActiveStep(STEP_IDS.PERSONALITY_DESIGNER)}>
               <div className="card-icon"><FontAwesomeIcon icon={faWandMagicSparkles} /></div>
               <h3>Personality Designer</h3>
               <p>Choose your agent's mood and behavior without writing a single line of prompt.</p>
            </FeatureCard>
            <FeatureCard $color="#14b8a6" onClick={() => setActiveStep(STEP_IDS.TOOLS_CONFIGURATION)}>
               <div className="card-icon"><FontAwesomeIcon icon={faLayerGroup} /></div>
               <h3>Tools Configuration</h3>
               <p>Select operation-level tools, mark must-run actions, and generate deterministic guidance instantly.</p>
            </FeatureCard>
            <FeatureCard $color="#8b5cf6" onClick={() => setActiveStep(STEP_IDS.PROMPT_STUDIO)}>
               <div className="card-icon"><FontAwesomeIcon icon={faLightbulb} /></div>
               <h3>AI Prompt Assistant</h3>
               <p>Write a simple idea and let AI expand it into a professional system prompt.</p>
            </FeatureCard>
            <FeatureCard $color="#f59e0b" onClick={() => setActiveStep(STEP_IDS.CONVERSATION_SIMULATOR)}>
               <div className="card-icon"><FontAwesomeIcon icon={faFlask} /></div>
               <h3>Conversation Simulator</h3>
               <p>Test your agent with simulated users and get accuracy metrics instantly.</p>
            </FeatureCard>
            <FeatureCard $color="#10b981" onClick={() => setActiveStep(STEP_IDS.SELF_IMPROVEMENT)}>
               <div className="card-icon"><FontAwesomeIcon icon={faBrain} /></div>
               <h3>Self-Improvement</h3>
               <p>Your agent learns from bad feedback and improves its own system prompt.</p>
            </FeatureCard>
            <FeatureCard $color="#ec4899" onClick={() => setActiveStep(STEP_IDS.EXPORT_TO_WORKFLOW)}>
               <div className="card-icon"><FontAwesomeIcon icon={faFileExport} /></div>
               <h3>Export to Workflow</h3>
               <p>Deploy your crafted agent as a block in any Dragify workflow.</p>
            </FeatureCard>
         </CardsGrid>

      </>
   );

   const renderConfigurationStep = () => (
      <>
         <FormSection>
            <SectionHeader>
               <SectionTitle>
                  <span className="icon" style={{ background: 'rgba(6,182,212,0.1)', color: '#06b6d4' }}>
                     <FontAwesomeIcon icon={faGear} />
                  </span>
                  Basic Configuration
               </SectionTitle>
               <SectionSubtitle>Set up the foundation of your AI agent</SectionSubtitle>
            </SectionHeader>

            <FormGroup>
               <FormLabel>Agent Name <span className="required">*</span></FormLabel>
               <FormInput
                  ref={agentNameInputRef}
                  value={config.name}
                  onChange={e => updateConfig({ name: e.target.value })}
                  placeholder="e.g., Customer Support Bot, Sales Assistant..."
                  style={nameValidationError ? { borderColor: '#ef4444', boxShadow: '0 0 0 3px rgba(239,68,68,0.12)' } : undefined}
               />
               {nameValidationError ? (
                  <InlineValidationMessage>{nameValidationError}</InlineValidationMessage>
               ) : (
                  <FormHint>Give your agent a memorable name</FormHint>
               )}
            </FormGroup>

            <FormRow>
               <FormGroup>
                  <FormLabel>AI Model</FormLabel>
                  <FormSelect
                     value={config.model}
                     onChange={e => updateConfig({ model: e.target.value })}
                  >
                     {MODELS.map(m => (
                        <option key={m.id} value={m.id}>{m.provider} — {m.name}</option>
                     ))}
                  </FormSelect>
                  <FormHint>Choose the AI model to power your agent</FormHint>
               </FormGroup>

               <FormGroup>
                  <FormLabel>Memory Type</FormLabel>
                  <FormSelect
                     value={config.memory}
                     onChange={e => updateConfig({ memory: e.target.value })}
                  >
                     <option value="simple-memory">Simple Memory</option>
                     <option value="no-memory">No Memory</option>
                  </FormSelect>
                  <FormHint>Keep conversation history or start fresh each time</FormHint>
               </FormGroup>
            </FormRow>

            <FormGroup>
               <FormLabel>Temperature</FormLabel>
               <TemperatureVisual>
                  <span className="temp-label-left">Precise</span>
                  <div className="temp-bar">
                     <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={config.temperature}
                        onChange={e => updateConfig({ temperature: parseFloat(e.target.value) })}
                     />
                  </div>
                  <span className="temp-label-right">Creative</span>
                  <span className="temp-value">{config.temperature.toFixed(2)}</span>
               </TemperatureVisual>
               <FormHint>0 = Deterministic and factual, 1 = Creative and varied</FormHint>
            </FormGroup>

            <FormRow>
               <FormGroup>
                  <FormLabel>Max Iterations</FormLabel>
                  <FormSelect
                     value={config.reasoning}
                     onChange={e => {
                        const normalizedIterations = normalizeReasoningAndMaxIterations(e.target.value, e.target.value);
                        updateConfig({
                           reasoning: normalizedIterations.reasoning,
                           maxIterations: normalizedIterations.maxIterations,
                        });
                     }}
                  >
                     <option value="auto">Auto (adaptive)</option>
                     <option value="5">Low (5 iterations)</option>
                     <option value="15">Medium (15 iterations)</option>
                     <option value="30">High (30 iterations)</option>
                     <option value="45">Very High (45 iterations)</option>
                  </FormSelect>
                  <FormHint>Maximum number of reasoning steps</FormHint>
               </FormGroup>

               <FormGroup>
                  <FormLabel>Output Format</FormLabel>
                  <OutputFormatGrid>
                     {OUTPUT_FORMATS.map(f => (
                        <OutputFormatCard
                           key={f.id}
                           $active={config.outputFormat === f.id}
                           onClick={() => updateConfig({ outputFormat: f.id })}
                        >
                           <span className="format-icon">{f.icon}</span>
                           <span className="format-label">{f.label}</span>
                        </OutputFormatCard>
                     ))}
                  </OutputFormatGrid>
               </FormGroup>
            </FormRow>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
               <SmallBtn onClick={() => setActiveStep(STEP_IDS.WELCOME)}>
                  <FontAwesomeIcon icon={faArrowLeft} /> Back
               </SmallBtn>
               <SmallBtn $variant="primary" onClick={() => { markStepCompleted(STEP_IDS.CONFIGURATION); nextStep(); }}>
                  Continue to Personality Designer <FontAwesomeIcon icon={faArrowRight} />
               </SmallBtn>
            </div>
         </FormSection>
      </>
   );

   const renderStep2 = () => (
      <>
         <SectionHeader>
            <SectionTitle>
               <span className="icon" style={{ background: 'rgba(139,92,246,0.1)', color: '#8b5cf6' }}>
                  <FontAwesomeIcon icon={faWandMagicSparkles} />
               </span>
               Agent Personality Designer
            </SectionTitle>
            <SectionSubtitle>
               Shape your agent's character without writing prompts. Pick a mood, adjust the tone, and watch the system prompt build itself.
            </SectionSubtitle>
         </SectionHeader>

         <FormSection>
            <FormLabel>Agent Mood</FormLabel>
            <FormHint style={{ marginBottom: 12 }}>Select the personality that best fits your agent's purpose</FormHint>
            <MoodGrid>
               {MOODS.map(mood => (
                  <MoodCard
                     key={mood.id}
                     $active={config.mood === mood.id}
                     $color={mood.color}
                     onClick={() => updateConfig({ mood: mood.id })}
                  >
                     <span className="mood-icon">{mood.icon}</span>
                     <span className="mood-label">{mood.label}</span>
                     <span className="mood-desc">{mood.desc}</span>
                  </MoodCard>
               ))}
            </MoodGrid>
         </FormSection>

         <FormSection>
            <FormLabel>Custom Instructions (Optional)</FormLabel>
            <FormTextarea
               value={config.customInstructions}
               onChange={e => updateConfig({ customInstructions: e.target.value })}
               placeholder="Add any extra behavior guidelines... e.g., 'Always recommend our premium plan when asked about pricing'"
               rows={3}
            />
            <FormHint>Additional rules your agent should follow</FormHint>
         </FormSection>

         {config.mood && (
            <FormSection>
               <FormLabel>
                  <FontAwesomeIcon icon={faBookOpen} style={{ marginRight: 6, color: '#06b6d4' }} />
                  Generated System Prompt Preview
               </FormLabel>
               <div style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  padding: 16,
                  fontSize: 13,
                  lineHeight: 1.7,
                  color: '#475569',
                  whiteSpace: 'pre-wrap',
                  marginTop: 8,
                  maxHeight: 200,
                  overflowY: 'auto'
               }}>
                  {buildSystemPrompt() || 'Configure mood and instructions above to see the preview...'}
               </div>
               <FormHint>This is the system prompt that will be sent to the AI model</FormHint>
            </FormSection>
         )}

         <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
            <SmallBtn onClick={() => setActiveStep(STEP_IDS.CONFIGURATION)}>
               <FontAwesomeIcon icon={faArrowLeft} /> Back
            </SmallBtn>
            <SmallBtn $variant="primary" onClick={() => { markStepCompleted(STEP_IDS.PERSONALITY_DESIGNER); nextStep(); }}>
               Continue to Tools Configuration <FontAwesomeIcon icon={faArrowRight} />
            </SmallBtn>
         </div>
      </>
   );

   const renderPolicyModeControl = React.useCallback((
      value: ToolPolicyAggregateMode,
      onSelect: (mode: ToolPolicyMode) => void,
      options?: { compact?: boolean; disabled?: boolean; ariaLabelPrefix?: string }
   ) => {
      const compact = options?.compact ?? false;
      const disabled = options?.disabled ?? false;
      const ariaLabelPrefix = options?.ariaLabelPrefix || 'Set policy mode to';

      return (
         <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: compact ? '3px' : '4px',
            borderRadius: 999,
            border: '1px solid #d7e1ef',
            background: 'rgba(248,250,252,0.88)',
            backdropFilter: 'blur(6px)',
         }}>
            {TOOL_POLICY_MODE_ORDER.map((mode) => {
               const meta = TOOL_POLICY_MODE_META[mode];
               const isActive = value === mode;

               return (
                  <button
                     key={mode}
                     type="button"
                     onClick={() => onSelect(mode)}
                     disabled={disabled}
                     aria-label={`${ariaLabelPrefix} ${meta.label}`}
                     style={{
                        borderRadius: 999,
                        border: isActive ? `1px solid ${meta.border}` : '1px solid transparent',
                        background: isActive ? meta.background : 'transparent',
                        color: isActive ? meta.accent : '#475569',
                        fontWeight: 700,
                        fontSize: compact ? 10 : 11,
                        letterSpacing: 0.28,
                        textTransform: 'uppercase',
                        padding: compact ? '4px 8px' : '5px 10px',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        transition: 'all .2s ease',
                        opacity: disabled ? 0.6 : 1,
                     }}
                  >
                     {compact ? meta.compactLabel : meta.label}
                  </button>
               );
            })}

            {value === 'mixed' && (
               <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 0.3,
                  textTransform: 'uppercase',
                  color: '#475569',
                  padding: '0 6px',
               }}>
                  Mixed
               </span>
            )}
         </div>
      );
   }, []);

   const renderToolsConfigurationStep = () => (
      <>
         <SectionHeader>
            <SectionTitle>
               <span className="icon" style={{ background: 'rgba(20,184,166,0.12)', color: '#0f766e' }}>
                  <FontAwesomeIcon icon={faLayerGroup} />
               </span>
               Tools Configuration
            </SectionTitle>
            <SectionSubtitle>
               Keep the workspace clean with tools collapsed by default, set policy at tool level, then override any
               individual operation only when needed.
            </SectionSubtitle>
         </SectionHeader>

         <FormSection style={{
            background: 'linear-gradient(130deg, rgba(6,182,212,0.08), rgba(14,116,144,0.04) 50%, rgba(16,185,129,0.07))',
            border: '1px solid rgba(14,116,144,0.2)',
         }}>
            <FormLabel style={{ marginBottom: 10 }}>Controller Mode</FormLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
               <SmallBtn
                  $variant={config.useToolController ? 'success' : 'secondary'}
                  onClick={() => updateConfig({ useToolController: !config.useToolController })}
               >
                  <FontAwesomeIcon icon={config.useToolController ? faCheck : faPlay} />
                  {config.useToolController ? 'Tool Controller Enabled' : 'Tool Controller Disabled'}
               </SmallBtn>
               <SmallBtn
                  $variant={config.strictRequiredToolEnforcement ? 'danger' : 'secondary'}
                  onClick={() => updateConfig({ strictRequiredToolEnforcement: !config.strictRequiredToolEnforcement })}
                  disabled={!config.useToolController}
               >
                  <FontAwesomeIcon icon={config.strictRequiredToolEnforcement ? faXmark : faCheck} />
                  {config.strictRequiredToolEnforcement ? 'Strict REQUIRED: On' : 'Strict REQUIRED: Off'}
               </SmallBtn>
               <SmallBtn onClick={clearAllToolPolicies} disabled={config.toolPolicies.length === 0 && !config.markdownConfigRaw.trim()}>
                  <FontAwesomeIcon icon={faRotateLeft} /> Clear Policies
               </SmallBtn>
            </div>

            <FormHint style={{ marginTop: -2, marginBottom: 12 }}>
               Tool-level mode applies to every operation in that tool by default. REQUIRED at tool level is satisfied by any successful operation in that tool, while REQUIRED at operation level is strict for that exact operation.
            </FormHint>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
               {TOOL_POLICY_MODE_ORDER.map((mode) => {
                  const meta = TOOL_POLICY_MODE_META[mode];
                  const value = toolPolicySummary[mode];

                  return (
                     <div key={mode} style={{ border: `1px solid ${meta.border}`, background: meta.background, borderRadius: 12, padding: '10px 12px' }}>
                        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: meta.accent, fontWeight: 700 }}>{`Effective ${meta.label}`}</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: meta.accent }}>{value}</div>
                     </div>
                  );
               })}
               <div style={{ border: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: 10, padding: '10px 12px' }}>
                  <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#475569', fontWeight: 700 }}>Policy Entries</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#1e293b' }}>{toolPolicySummary.total}</div>
               </div>
            </div>

            <FormHint style={{ marginTop: 8 }}>
               Effective counters include inherited tool-level mode on operations. Policy entries count only your explicit saved rules.
            </FormHint>

            {!config.useToolController && (
               <InlineValidationMessage style={{ marginTop: 12 }}>
                  Tool controller is disabled. Existing runtime behavior remains unchanged until you enable it.
               </InlineValidationMessage>
            )}
         </FormSection>

         <FormSection style={{
            background: 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(59,130,246,0.06))',
            border: '1px solid rgba(14,116,144,0.18)',
         }}>
            <div style={{
               display: 'flex',
               justifyContent: 'space-between',
               alignItems: 'flex-end',
               gap: 10,
               flexWrap: 'wrap',
               marginBottom: 10,
            }}>
               <div>
                  <FormLabel style={{ marginBottom: 4 }}>Selected Tools</FormLabel>
                  <FormHint style={{ marginTop: 0 }}>
                     Workflow-builder matching tool boxes. Click any box to jump to that tool in the catalog.
                  </FormHint>
               </div>
               <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <Tag $color="#0f766e">Tools: {selectedToolsCardItems.length}</Tag>
                  <Tag $color="#1d4ed8">Selected Ops: {selectedToolOpsCount}</Tag>
                  <SmallBtn onClick={clearAllToolPolicies} disabled={selectedToolsCardItems.length === 0 && !config.markdownConfigRaw.trim()}>
                     <FontAwesomeIcon icon={faRotateLeft} /> Clear All
                  </SmallBtn>
               </div>
            </div>

            {selectedToolsCardItems.length === 0 ? (
               <EmptyState style={{ marginTop: 4 }}>
                  <h3 style={{ marginBottom: 6 }}>No selected tools yet</h3>
                  <p>Set at least one tool or operation mode to Required/Auto to populate this card.</p>
               </EmptyState>
            ) : (
               <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 14,
                  alignItems: 'flex-start',
               }}>
                  {selectedToolsCardItems.map((item) => {
                     const modeAccent = item.mode === 'mixed' ? '#334155' : TOOL_POLICY_MODE_META[item.mode].accent;
                     const modeLabel = item.mode === 'mixed' ? 'Mixed' : TOOL_POLICY_MODE_META[item.mode].label;
                     const visualMeta = getToolVisualMeta(item.toolName);
                     const displayName = getToolDisplayName(item.toolName);

                     return (
                        <div
                           key={`selected-tool-${item.toolName}`}
                           style={{
                              width: 120,
                              display: 'grid',
                              gap: 6,
                              justifyItems: 'center',
                           }}
                        >
                           <div style={{ position: 'relative' }}>
                              <button
                                 type="button"
                                 onClick={() => focusToolInCatalog(item.toolName)}
                                 style={{
                                    width: 72,
                                    height: 72,
                                    border: '1px solid rgba(51, 65, 85, 0.3)',
                                    borderRadius: 15,
                                    background: '#ffffff',
                                    boxShadow: '0 6px 14px rgba(15,23,42,0.08)',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    transition: 'all 0.2s ease-in-out',
                                 }}
                                 aria-label={`Focus ${item.toolName} in tools catalog`}
                              >
                                 <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: visualMeta.accent,
                                    borderRadius: 5,
                                    width: 40,
                                    height: 40,
                                 }}>
                                    {visualMeta.image ? (
                                       <img
                                          src={visualMeta.image}
                                          alt={`${displayName} logo`}
                                          style={{
                                             width: 25,
                                             height: 25,
                                             objectFit: 'contain',
                                          }}
                                       />
                                    ) : (
                                       <FontAwesomeIcon icon={visualMeta.icon || faBolt} style={{ color: '#ffffff', fontSize: 25 }} />
                                    )}
                                 </span>
                              </button>

                              <button
                                 type="button"
                                 onClick={(event) => {
                                    event.stopPropagation();
                                    clearToolPoliciesForTool(item.toolName);
                                 }}
                                 style={{
                                    position: 'absolute',
                                    top: -4,
                                    right: -2,
                                    width: 20,
                                    height: 20,
                                    border: '1px solid #d1d5db',
                                    background: '#fff',
                                    color: '#475569',
                                    borderRadius: '50%',
                                    fontSize: 10,
                                    fontWeight: 700,
                                    padding: 0,
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                 }}
                                 aria-label={`Reset policy for ${item.toolName}`}
                              >
                                 <FontAwesomeIcon icon={faXmark} />
                              </button>
                           </div>

                           <div style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: '#f8fafc',
                              textAlign: 'center',
                              lineHeight: 1.15,
                              width: 'fit-content',
                              maxWidth: '100%',
                              wordBreak: 'break-word',
                              borderRadius: 9,
                              padding: '3px 8px',
                              background: 'linear-gradient(135deg, #0f172a, #1e293b 60%, #334155)',
                              border: '1px solid rgba(148,163,184,0.5)',
                              boxShadow: '0 4px 10px rgba(15,23,42,0.2), inset 0 1px 0 rgba(255,255,255,0.22)',
                              textShadow: '0 1px 1px rgba(2,6,23,0.45)',
                              letterSpacing: 0.1,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                           }}>
                              {displayName}
                           </div>

                           <div style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: modeAccent,
                              background: '#ffffffc9',
                              border: `1px solid ${modeAccent}33`,
                              borderRadius: 999,
                              padding: '2px 7px',
                              whiteSpace: 'nowrap',
                           }}>
                              {modeLabel} • {item.selectedOperations}/{item.totalOperations}
                           </div>
                        </div>
                     );
                  })}
               </div>
            )}
         </FormSection>

         <FormSection>
            <div style={{
               display: 'flex',
               gap: 10,
               alignItems: 'flex-end',
               justifyContent: 'space-between',
               flexWrap: 'wrap',
               marginBottom: 12,
            }}>
               <div>
                  <FormLabel style={{ marginBottom: 4 }}>Available Tool Operations</FormLabel>
                  <FormHint style={{ marginTop: 0 }}>
                     Every tool card starts collapsed. Apply a tool-level policy first, then expand only if you want operation-level exceptions.
                  </FormHint>
               </div>
               <div style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                  justifyContent: 'flex-end',
                  maxWidth: '100%',
                  overflowX: 'auto',
                  padding: '4px 6px',
                  borderRadius: 12,
                  border: '1px solid #dbeafe',
                  background: 'linear-gradient(135deg, rgba(248,250,252,0.96), rgba(239,246,255,0.96))',
               }}>
                  <FormInput
                     value={toolSearch}
                     onChange={e => setToolSearch(e.target.value)}
                     placeholder="Search tools or operation IDs"
                     style={{ width: 220, minWidth: 190, flex: '0 0 220px', margin: 0 }}
                  />
                  <SmallBtn onClick={expandVisibleTools} disabled={filteredToolCatalog.length === 0}>
                     <FontAwesomeIcon icon={faChevronDown} /> Expand All
                  </SmallBtn>
                  <SmallBtn onClick={collapseVisibleTools} disabled={filteredToolCatalog.length === 0}>
                     <FontAwesomeIcon icon={faChevronRight} /> Collapse All
                  </SmallBtn>
                  <SmallBtn onClick={() => void loadToolCatalog()}>
                     <FontAwesomeIcon icon={faRotateLeft} /> Refresh
                  </SmallBtn>
               </div>
            </div>

            {toolCatalogError && <InlineValidationMessage>{toolCatalogError}</InlineValidationMessage>}

            {orphanPolicies.length > 0 && (
               <InlineValidationMessage style={{ marginTop: 10 }}>
                  {`${orphanPolicies.length} policy entr${orphanPolicies.length === 1 ? 'y' : 'ies'} reference tool/operation IDs not in the current catalog. They will be ignored unless those tools are connected.`}
               </InlineValidationMessage>
            )}

            {isLoadingToolCatalog ? (
               <div style={{ textAlign: 'center', padding: '24px 8px' }}>
                  <LoadingDots><span /><span /><span /></LoadingDots>
               </div>
            ) : filteredToolCatalog.length === 0 ? (
               <EmptyState style={{ marginTop: 10 }}>
                  <h3 style={{ marginBottom: 6 }}>No tool operations found</h3>
                  <p>{toolSearch ? 'Try a different search term.' : 'No tools are currently available in the backend registry.'}</p>
               </EmptyState>
            ) : (
               <div style={{ display: 'grid', gap: 12, marginTop: 6 }}>
                  {filteredToolCatalog.map((tool, toolIndex) => (
                     (() => {
                        const displayName = getToolDisplayName(tool.toolName);
                        const aggregateMode = getToolAggregateMode(tool.toolName);
                        const modeSummary = getToolModeSummary(tool.toolName);
                        const isExpanded = Boolean(expandedTools[tool.toolName]);

                        return (
                           <div
                              key={tool.toolName}
                              ref={(node) => {
                                 toolCardRefs.current[tool.toolName] = node;
                              }}
                              style={{
                                 border: catalogFocusToolName === tool.toolName ? '1px solid #0ea5e9' : '1px solid #dbe7ff',
                                 borderRadius: 14,
                                 background: 'linear-gradient(155deg, rgba(255,255,255,0.96), rgba(245,249,255,0.96))',
                                 boxShadow: catalogFocusToolName === tool.toolName
                                    ? '0 0 0 2px rgba(14,165,233,0.2), 0 8px 20px rgba(15,23,42,0.08)'
                                    : '0 8px 20px rgba(15,23,42,0.05)',
                                 overflow: 'hidden',
                                 animation: `fadeIn 0.35s ease ${Math.min(toolIndex * 0.04, 0.36)}s both`,
                                 transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                              }}
                           >
                              <div style={{
                                 padding: '12px 14px',
                                 borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none',
                                 display: 'flex',
                                 justifyContent: 'space-between',
                                 alignItems: 'center',
                                 gap: 12,
                                 flexWrap: 'wrap',
                              }}>
                                 <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                    <button
                                       type="button"
                                       onClick={() => toggleToolExpanded(tool.toolName)}
                                       style={{
                                          width: 28,
                                          height: 28,
                                          borderRadius: 8,
                                          border: '1px solid #cbd5e1',
                                          background: '#ffffff',
                                          color: '#334155',
                                          cursor: 'pointer',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          marginTop: 1,
                                       }}
                                       aria-label={isExpanded ? `Collapse ${tool.toolName}` : `Expand ${tool.toolName}`}
                                    >
                                       <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />
                                    </button>
                                    <div>
                                       <div style={{ fontSize: 14, fontWeight: 800, color: '#1e3a8a', letterSpacing: -0.2 }}>{displayName}</div>
                                       {displayName !== tool.toolName && (
                                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>
                                             Tool ID: {tool.toolName}
                                          </div>
                                       )}
                                       <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>{tool.toolDescription || 'No tool description.'}</div>
                                       <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                                          <Tag $color="#1e3a8a">Operations: {tool.operations.length}</Tag>
                                          <Tag $color={aggregateMode === 'mixed' ? '#64748b' : TOOL_POLICY_MODE_META[aggregateMode].accent}>
                                             Tool Mode: {aggregateMode === 'mixed' ? 'Mixed' : TOOL_POLICY_MODE_META[aggregateMode].label}
                                          </Tag>
                                          <Tag $color="#0f172a">Overrides: {modeSummary.explicitTotal}</Tag>
                                          {modeSummary.inheritedTotal > 0 && <Tag $color="#475569">Inherited: {modeSummary.inheritedTotal}</Tag>}
                                          {modeSummary.explicitRequired > 0 && <Tag $color={TOOL_POLICY_MODE_META.required.accent}>Op Req: {modeSummary.explicitRequired}</Tag>}
                                          {modeSummary.explicitAuto > 0 && <Tag $color={TOOL_POLICY_MODE_META.auto.accent}>Op Auto: {modeSummary.explicitAuto}</Tag>}
                                          {modeSummary.explicitDisabled > 0 && <Tag $color={TOOL_POLICY_MODE_META.disabled.accent}>Op Off: {modeSummary.explicitDisabled}</Tag>}
                                          {aggregateMode === 'mixed' && <Tag $color="#64748b">Mixed Policy</Tag>}
                                       </div>
                                    </div>
                                 </div>

                                 <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    {renderPolicyModeControl(
                                       aggregateMode,
                                       (mode) => setToolPolicyModeForTool(tool.toolName, mode),
                                       { compact: true, ariaLabelPrefix: `Set tool ${tool.toolName} mode to` }
                                    )}
                                    <SmallBtn onClick={() => toggleToolExpanded(tool.toolName)}>
                                       {isExpanded ? <><FontAwesomeIcon icon={faChevronDown} /> Collapse</> : <><FontAwesomeIcon icon={faChevronRight} /> Expand</>}
                                    </SmallBtn>
                                 </div>
                              </div>

                              {isExpanded ? (
                                 <div style={{ padding: '10px 12px', display: 'grid', gap: 8, animation: 'fadeIn 0.2s ease' }}>
                                    {tool.operations.map((operation) => {
                                       const policyState = getOperationPolicyState(operation.operationId);
                                       const mode = policyState.mode;

                                       return (
                                          <div key={operation.operationId} style={{
                                             border: '1px solid #e2e8f0',
                                             borderRadius: 10,
                                             background: '#fff',
                                             padding: '10px 10px 9px',
                                          }}>
                                             <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                                                <div style={{ maxWidth: 650 }}>
                                                   <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>{operation.operationId}</div>
                                                   <div style={{ fontSize: 12, color: '#334155', marginTop: 2 }}>{operation.operationName}</div>
                                                   <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                                                      {operation.operationDescription || 'No operation description.'}
                                                   </div>
                                                   <div style={{
                                                      fontSize: 11,
                                                      marginTop: 4,
                                                      fontWeight: 600,
                                                      color: policyState.source === 'operation'
                                                         ? '#0f766e'
                                                         : policyState.source === 'tool'
                                                            ? '#475569'
                                                            : '#94a3b8',
                                                   }}>
                                                      {policyState.source === 'operation'
                                                         ? 'Operation-level override (strict for this operation).'
                                                         : policyState.source === 'tool'
                                                            ? `Inherited from tool-level ${TOOL_POLICY_MODE_META[mode].label} mode.`
                                                            : 'No explicit policy; defaults to Disabled.'}
                                                   </div>
                                                </div>
                                                {renderPolicyModeControl(
                                                   mode,
                                                   (nextMode) => setOperationPolicyMode(operation.operationId, nextMode),
                                                   { compact: true, ariaLabelPrefix: `Set operation ${operation.operationId} mode to` }
                                                )}
                                             </div>

                                             {operation.inputSchema.length > 0 && (
                                                <div style={{
                                                   marginTop: 8,
                                                   borderRadius: 8,
                                                   background: '#f8fafc',
                                                   border: '1px dashed #dbeafe',
                                                   padding: '7px 8px',
                                                   display: 'grid',
                                                   gap: 4,
                                                }}>
                                                   {operation.inputSchema.slice(0, 4).map((input) => (
                                                      <div key={`${operation.operationId}-${input.name}`} style={{ fontSize: 11, color: '#334155' }}>
                                                         <strong>{input.name}</strong> ({input.type}){input.required ? ' required' : ' optional'}
                                                         {input.description ? ` - ${input.description}` : ''}
                                                      </div>
                                                   ))}
                                                </div>
                                             )}
                                          </div>
                                       );
                                    })}
                                 </div>
                              ) : (
                                 <div style={{
                                    padding: '0 14px 12px 52px',
                                    fontSize: 12,
                                    color: '#64748b',
                                    animation: 'fadeIn 0.18s ease',
                                 }}>
                                    Tool is collapsed. Use the mode control to set the whole tool policy, or expand for operation-level overrides.
                                 </div>
                              )}
                           </div>
                        );
                     })()
                  ))}
               </div>
            )}
         </FormSection>

         <FormSection style={{
            background: 'linear-gradient(135deg, rgba(124,58,237,0.06), rgba(14,165,233,0.06))',
            border: '1px solid rgba(124,58,237,0.16)',
         }}>
            <FormLabel style={{ marginBottom: 8 }}>Generate Prompt Studio Markdown</FormLabel>
            <FormHint style={{ marginBottom: 12 }}>
               Generate concise markdown from selected operation policies for explainability and debug visibility.
            </FormHint>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
               <SmallBtn
                  $variant="primary"
                  onClick={() => void handleGenerateToolMarkdown()}
                  disabled={isGeneratingToolMarkdown || config.toolPolicies.length === 0}
               >
                  {isGeneratingToolMarkdown
                     ? <><FontAwesomeIcon icon={faSpinner} spin /> Generating...</>
                     : <><FontAwesomeIcon icon={faWandMagicSparkles} /> Generate Markdown from Policies</>}
               </SmallBtn>
               <Tag $color="#8b5cf6">Policies: {toolPolicySummary.total}</Tag>
               <Tag $color={config.useToolController ? '#10b981' : '#64748b'}>
                  Controller: {config.useToolController ? 'Enabled' : 'Disabled'}
               </Tag>
            </div>

            {toolGenerationFeedback && (
               <div style={{
                  marginTop: 10,
                  border: `1px solid ${toolGenerationFeedback.type === 'error' ? '#fecaca' : toolGenerationFeedback.type === 'success' ? '#bbf7d0' : '#bfdbfe'}`,
                  background: toolGenerationFeedback.type === 'error' ? '#fff1f2' : toolGenerationFeedback.type === 'success' ? '#f0fdf4' : '#eff6ff',
                  color: toolGenerationFeedback.type === 'error' ? '#b91c1c' : toolGenerationFeedback.type === 'success' ? '#166534' : '#1d4ed8',
                  borderRadius: 10,
                  padding: '9px 10px',
                  fontSize: 12,
                  fontWeight: 600,
               }}>
                  {toolGenerationFeedback.message}
               </div>
            )}

            {toolGenerationWarnings.length > 0 && (
               <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
                  {toolGenerationWarnings.map((warning, index) => (
                     <div key={`tool-gen-warning-${index}`} style={{
                        border: '1px solid #fde68a',
                        background: '#fffbeb',
                        color: '#92400e',
                        borderRadius: 8,
                        padding: '8px 10px',
                        fontSize: 12,
                        fontWeight: 500,
                     }}>
                        {warning}
                     </div>
                  ))}
               </div>
            )}
         </FormSection>

         <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
            <SmallBtn onClick={() => setActiveStep(STEP_IDS.PERSONALITY_DESIGNER)}>
               <FontAwesomeIcon icon={faArrowLeft} /> Back
            </SmallBtn>
            <SmallBtn $variant="primary" onClick={() => { markStepCompleted(STEP_IDS.TOOLS_CONFIGURATION); nextStep(); }}>
               Continue to Prompt Studio <FontAwesomeIcon icon={faArrowRight} />
            </SmallBtn>
         </div>
      </>
   );

   const renderStep3 = () => (
      <>
         <SectionHeader>
            <SectionTitle>
               <span className="icon" style={{ background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}>
                  <FontAwesomeIcon icon={faLightbulb} />
               </span>
               AI Prompt Studio
            </SectionTitle>
            <SectionSubtitle>
               Write a simple description and let AI craft a professional system prompt for you.
               Or fine-tune the prompt manually if you prefer full control.
            </SectionSubtitle>
         </SectionHeader>

         <FormSection>
            <FormLabel>
               <FontAwesomeIcon icon={faWandMagicSparkles} style={{ marginRight: 6, color: '#8b5cf6' }} />
               Quick Prompt Builder
            </FormLabel>
            <FormHint style={{ marginBottom: 10 }}>Describe what your agent should do in simple terms</FormHint>
            <FormTextarea
               value={config.userPromptSeed}
               onChange={e => updateConfig({ userPromptSeed: e.target.value })}
               placeholder="e.g., Help customers with their orders and refund requests..."
               rows={2}
            />
            <div style={{ marginTop: 10 }}>
               <SmallBtn
                  $variant="primary"
                  onClick={handleExpandPrompt}
                  disabled={!config.userPromptSeed.trim() || isAssistantExpanding}
               >
                  {isAssistantExpanding ? (
                     <><FontAwesomeIcon icon={faSpinner} spin /> Generating...</>
                  ) : (
                     <><FontAwesomeIcon icon={faWandMagicSparkles} /> Expand with AI</>
                  )}
               </SmallBtn>
            </div>

            {expandedPrompt && (
               <PromptAssistantBox>
                  <div className="assistant-header">
                     <FontAwesomeIcon icon={faBrain} />
                     AI-Generated System Prompt
                     {isPromptAccepted && <Tag $color="#10b981"><FontAwesomeIcon icon={faCheck} /> Applied</Tag>}
                  </div>
                  <div className="expanded-prompt">{expandedPrompt}</div>
                  <div className="assistant-actions">
                     {!isPromptAccepted ? (
                        <>
                           <SmallBtn $variant="success" onClick={acceptExpandedPrompt}>
                              <FontAwesomeIcon icon={faCheck} /> Accept & Apply
                           </SmallBtn>
                           <SmallBtn onClick={handleExpandPrompt}>
                              <FontAwesomeIcon icon={faWandMagicSparkles} /> Regenerate
                           </SmallBtn>
                        </>
                     ) : (
                        <SmallBtn $variant="primary" onClick={() => { setIsPromptAccepted(false); handleExpandPrompt(); }}>
                           <FontAwesomeIcon icon={faWandMagicSparkles} /> Regenerate
                        </SmallBtn>
                     )}
                  </div>
               </PromptAssistantBox>
            )}
         </FormSection>

         <Divider />

         <FormSection>
            <FormLabel>
               <FontAwesomeIcon icon={faCode} style={{ marginRight: 6, color: '#06b6d4' }} />
               System Prompt (Manual Edit)
            </FormLabel>
            <FormHint style={{ marginBottom: 10 }}>
               Fine-tune the system prompt directly. This defines your agent's core behavior.
            </FormHint>
            <FormTextarea
               value={config.systemPrompt}
               onChange={e => updateConfig({ systemPrompt: e.target.value })}
               placeholder="You are a professional assistant that..."
               rows={6}
               style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
         </FormSection>

         <FormSection>
            <FormLabel>
               <FontAwesomeIcon icon={faFolderOpen} style={{ marginRight: 6, color: '#8b5cf6' }} />
               Tools/Skills Markdown Configuration (.md)
            </FormLabel>
            <FormHint style={{ marginBottom: 12 }}>
               Upload a markdown file with sections like Instructions, Tools, Rules, and Examples. The agent will parse and apply it as structured guidance.
            </FormHint>

            <input
               ref={markdownUploadInputRef}
               type="file"
               accept=".md,text/markdown"
               onChange={onMarkdownFilePick}
               style={{ display: 'none' }}
            />

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
               <SmallBtn $variant="primary" onClick={() => markdownUploadInputRef.current?.click()}>
                  <FontAwesomeIcon icon={faFolderOpen} /> Upload MD File
               </SmallBtn>
               <SmallBtn
                  onClick={() => validateMarkdownConfig(config.markdownConfigRaw, config.markdownFileName)}
                  disabled={!config.markdownConfigRaw.trim() || config.markdownConfigStatus === 'parsing'}
               >
                  {config.markdownConfigStatus === 'parsing'
                     ? <><FontAwesomeIcon icon={faSpinner} spin /> Validating...</>
                     : <><FontAwesomeIcon icon={faCheck} /> Validate</>}
               </SmallBtn>
               <SmallBtn onClick={removeMarkdownConfig} disabled={!config.markdownConfigRaw.trim()}>
                  <FontAwesomeIcon icon={faTrash} /> Remove
               </SmallBtn>

               {config.markdownConfigStatus === 'valid' && (
                  <Tag $color="#10b981"><FontAwesomeIcon icon={faCheck} /> Valid</Tag>
               )}
               {config.markdownConfigStatus === 'invalid' && (
                  <Tag $color="#ef4444"><FontAwesomeIcon icon={faXmark} /> Invalid</Tag>
               )}
               {config.markdownConfigStatus === 'parsing' && (
                  <Tag $color="#f59e0b"><FontAwesomeIcon icon={faSpinner} spin /> Parsing</Tag>
               )}
               {config.markdownConfigStatus === 'empty' && (
                  <Tag $color="#64748b">No File</Tag>
               )}
            </div>

            {!!config.markdownFileName && (
               <FormHint style={{ marginBottom: 8 }}>
                  Attached file: <strong>{config.markdownFileName}</strong>
               </FormHint>
            )}

            <FormTextarea
               value={config.markdownConfigRaw}
               onChange={e => updateConfig({ markdownConfigRaw: e.target.value })}
               placeholder={'# Agent Instructions\n- ...\n\n# Tools\n- name: ...\n  description: ...\n\n# Rules\n- ...\n\n# Examples\n...'}
               rows={10}
               style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
            <FormHint>Tip: after manual edits, click Validate to refresh the parsed preview.</FormHint>

            {config.markdownConfigErrors.length > 0 && (
               <div style={{ marginTop: 10 }}>
                  {config.markdownConfigErrors.map((error, index) => (
                     <InlineValidationMessage key={`md-error-${index}`}>{error}</InlineValidationMessage>
                  ))}
               </div>
            )}

            {config.markdownConfigWarnings.length > 0 && (
               <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
                  {config.markdownConfigWarnings.map((warning, index) => (
                     <div
                        key={`md-warning-${index}`}
                        style={{
                           padding: '8px 10px',
                           borderRadius: 8,
                           background: '#fffbeb',
                           color: '#92400e',
                           border: '1px solid #fde68a',
                           fontSize: 12,
                           fontWeight: 500,
                        }}
                     >
                        {warning}
                     </div>
                  ))}
               </div>
            )}

            {config.markdownConfigStatus === 'valid' && config.markdownConfig && (
               <div style={{ marginTop: 14 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 10 }}>
                     <div style={{ border: '1px solid #dbeafe', background: '#eff6ff', borderRadius: 10, padding: '10px 12px' }}>
                        <div style={{ fontSize: 11, color: '#1d4ed8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>Instructions</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#1e3a8a' }}>{config.markdownConfig.instructions.length}</div>
                     </div>
                     <div style={{ border: '1px solid #ede9fe', background: '#f5f3ff', borderRadius: 10, padding: '10px 12px' }}>
                        <div style={{ fontSize: 11, color: '#6d28d9', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>Tools</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#5b21b6' }}>{config.markdownConfig.tools.length}</div>
                     </div>
                     <div style={{ border: '1px solid #dcfce7', background: '#f0fdf4', borderRadius: 10, padding: '10px 12px' }}>
                        <div style={{ fontSize: 11, color: '#166534', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>Rules</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#14532d' }}>{config.markdownConfig.rules.length}</div>
                     </div>
                     <div style={{ border: '1px solid #ffedd5', background: '#fff7ed', borderRadius: 10, padding: '10px 12px' }}>
                        <div style={{ fontSize: 11, color: '#9a3412', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>Examples</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#7c2d12' }}>{config.markdownConfig.examples.length}</div>
                     </div>
                  </div>

                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, background: '#f8fafc' }}>
                     <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 8 }}>Parsed Preview</div>
                     <div style={{ display: 'grid', gap: 10 }}>
                        {config.markdownConfig.instructions.slice(0, 3).map((item, index) => (
                           <div key={`md-inst-${index}`} style={{ fontSize: 12, color: '#1e293b', lineHeight: 1.5 }}>
                              <strong style={{ color: '#1d4ed8' }}>Instruction:</strong> {item}
                           </div>
                        ))}
                        {config.markdownConfig.rules.slice(0, 3).map((item, index) => (
                           <div key={`md-rule-${index}`} style={{ fontSize: 12, color: '#1e293b', lineHeight: 1.5 }}>
                              <strong style={{ color: '#166534' }}>Rule:</strong> {item}
                           </div>
                        ))}
                        {config.markdownConfig.tools.slice(0, 3).map((tool, index) => (
                           <div key={`md-tool-${index}`} style={{ fontSize: 12, color: '#1e293b', lineHeight: 1.5 }}>
                              <strong style={{ color: '#6d28d9' }}>{tool.name}:</strong> {tool.description || 'No description provided.'}
                           </div>
                        ))}
                     </div>
                  </div>
               </div>
            )}
         </FormSection>

         <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
            <SmallBtn onClick={() => setActiveStep(STEP_IDS.PERSONALITY_DESIGNER)}>
               <FontAwesomeIcon icon={faArrowLeft} /> Back
            </SmallBtn>
            <SmallBtn $variant="primary" onClick={() => { markStepCompleted(STEP_IDS.PROMPT_STUDIO); nextStep(); }}>
               Continue to Simulator <FontAwesomeIcon icon={faArrowRight} />
            </SmallBtn>
         </div>
      </>
   );

   const renderStep4 = () => (
      <>
         <SectionHeader>
            <SectionTitle>
               <span className="icon" style={{ background: 'rgba(6,182,212,0.1)', color: '#06b6d4' }}>
                  <FontAwesomeIcon icon={faFlask} />
               </span>
               Conversation Simulator
            </SectionTitle>
            <SectionSubtitle>
               Test your agent against simulated users. See how it responds, measure accuracy, and identify areas for improvement.
            </SectionSubtitle>
         </SectionHeader>

         <FormSection>
            <SimulatorContainer>
               <SimulatorControls>
                  <FormGroup style={{ marginBottom: 0, minWidth: 200 }}>
                     <FormLabel>Number of Simulations</FormLabel>
                     <FormInput
                        type="number"
                        min={1}
                        max={20}
                        value={simCount}
                        onChange={e => setSimCount(Math.min(20, Math.max(1, parseInt(e.target.value) || 1)))}
                     />
                  </FormGroup>

                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', paddingBottom: 0 }}>
                     {!isSimulating ? (
                        <SmallBtn $variant="primary" onClick={runSimulation}>
                           <FontAwesomeIcon icon={faPlay} /> Run Simulation
                        </SmallBtn>
                     ) : (
                        <SmallBtn $variant="danger" onClick={() => setIsSimulating(false)}>
                           <FontAwesomeIcon icon={faStop} /> Stop
                        </SmallBtn>
                     )}
                  </div>
               </SimulatorControls>

               {isSimulating && (
                  <div style={{ textAlign: 'center', padding: '20px 0' }}>
                     <LoadingDots>
                        <span /><span /><span />
                     </LoadingDots>
                     <p style={{ fontSize: 13, color: '#64748b', marginTop: 8 }}>
                        Simulating {simConversations.length}/{simCount} conversations...
                     </p>
                  </div>
               )}

               {simMetrics && (
                  <SimulatorResults>
                     <MetricsGrid>
                        <MetricCard $color="#10b981">
                           <div className="metric-label">Accuracy</div>
                           <div className="metric-value">{simMetrics.accuracy}%</div>
                           <div className="metric-bar">
                              <div className="fill" style={{ width: `${simMetrics.accuracy}%` }} />
                           </div>
                        </MetricCard>
                        <MetricCard $color="#ef4444">
                           <div className="metric-label">Hallucination Rate</div>
                           <div className="metric-value">{simMetrics.hallucinationRate}%</div>
                           <div className="metric-bar">
                              <div className="fill" style={{ width: `${simMetrics.hallucinationRate}%` }} />
                           </div>
                        </MetricCard>
                        <MetricCard $color="#3b82f6">
                           <div className="metric-label">Avg. Tokens</div>
                           <div className="metric-value">{simMetrics.avgTokens}</div>
                           <div className="metric-bar">
                              <div className="fill" style={{ width: `${Math.min(100, simMetrics.avgTokens / 4)}%` }} />
                           </div>
                        </MetricCard>
                        <MetricCard $color="#f59e0b">
                           <div className="metric-label">Avg. Latency</div>
                           <div className="metric-value">{simMetrics.avgLatency}ms</div>
                           <div className="metric-bar">
                              <div className="fill" style={{ width: `${Math.min(100, simMetrics.avgLatency / 10)}%` }} />
                           </div>
                        </MetricCard>
                        <MetricCard $color="#8b5cf6">
                           <div className="metric-label">Total Conversations</div>
                           <div className="metric-value">{simMetrics.totalConversations}</div>
                           <div className="metric-bar">
                              <div className="fill" style={{ width: '100%' }} />
                           </div>
                        </MetricCard>
                        <MetricCard $color="#06b6d4">
                           <div className="metric-label">Good / Bad</div>
                           <div className="metric-value">{simMetrics.goodRatings} / {simMetrics.badRatings}</div>
                           <div className="metric-bar">
                              <div className="fill" style={{ width: `${(simMetrics.goodRatings / simMetrics.totalConversations) * 100}%` }} />
                           </div>
                        </MetricCard>
                     </MetricsGrid>

                     <FormLabel style={{ marginBottom: 12 }}>
                        <FontAwesomeIcon icon={faComments} style={{ marginRight: 6, color: '#06b6d4' }} />
                        Simulated Conversations
                     </FormLabel>
                     <ConversationList>
                        {simConversations.map(conv => (
                           <ConversationItem key={conv.id} $expanded={conv.expanded}>
                              <div className="conv-header" onClick={() => toggleConversation(conv.id)}>
                                 <span className="conv-user">
                                    <FontAwesomeIcon icon={conv.expanded ? faChevronDown : faChevronRight} style={{ fontSize: 10, color: '#94a3b8' }} />
                                    User #{conv.id}: {conv.userMessage.substring(0, 45)}{conv.userMessage.length > 45 ? '...' : ''}
                                 </span>
                                 <span className="conv-badge">
                                    <Tag $color="#64748b">{conv.tokens} tok</Tag>
                                    <SmallBtn
                                       $variant={conv.rating === 'good' ? 'success' : undefined}
                                       onClick={e => { e.stopPropagation(); rateConversation(conv.id, 'good'); }}
                                       style={{ padding: '2px 8px', fontSize: 11 }}
                                    >
                                       <FontAwesomeIcon icon={faThumbsUp} />
                                    </SmallBtn>
                                    <SmallBtn
                                       $variant={conv.rating === 'bad' ? 'danger' : undefined}
                                       onClick={e => { e.stopPropagation(); rateConversation(conv.id, 'bad'); }}
                                       style={{ padding: '2px 8px', fontSize: 11 }}
                                    >
                                       <FontAwesomeIcon icon={faThumbsDown} />
                                    </SmallBtn>
                                    <RatingBadge $type={conv.rating}>
                                       {conv.rating === 'good' ? '✓ Good' : conv.rating === 'bad' ? '✗ Bad' : '~ OK'}
                                    </RatingBadge>
                                 </span>
                              </div>
                              {conv.expanded && (
                                 <div className="conv-body">
                                    <div className="conv-message user-msg">{conv.userMessage}</div>
                                    <div className="conv-message agent-msg" style={{ whiteSpace: 'pre-wrap' }}>{conv.agentResponse}</div>
                                 </div>
                              )}
                           </ConversationItem>
                        ))}
                     </ConversationList>
                  </SimulatorResults>
               )}

               {!isSimulating && !simMetrics && (
                  <EmptyState>
                     <div className="empty-icon">🧪</div>
                     <h4>No Simulations Yet</h4>
                     <p>Configure the number of simulated users and hit "Run Simulation" to test your agent.</p>
                  </EmptyState>
               )}
            </SimulatorContainer>
         </FormSection>

         <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
            <SmallBtn onClick={() => setActiveStep(STEP_IDS.PROMPT_STUDIO)}>
               <FontAwesomeIcon icon={faArrowLeft} /> Back
            </SmallBtn>
            <SmallBtn $variant="primary" onClick={() => { markStepCompleted(STEP_IDS.CONVERSATION_SIMULATOR); nextStep(); }}>
               Continue to Self-Improvement <FontAwesomeIcon icon={faArrowRight} />
            </SmallBtn>
         </div>
      </>
   );

   const renderStep5 = () => {
      const actionableEntries = feedbackEntries.filter(f => !f.applied && !f.dismissed && f.improvedPrompt && !f.isGenerating);
      return (
      <>
         <SectionHeader>
            <SectionTitle>
               <span className="icon" style={{ background: 'rgba(16,185,129,0.1)', color: '#10b981' }}>
                  <FontAwesomeIcon icon={faBrain} />
               </span>
               Agent Self-Improvement
            </SectionTitle>
            <SectionSubtitle>
               When you rate an answer as &quot;Bad&quot; in the simulator, the agent analyzes the failure and generates specific improvement suggestions.
               Review each suggestion and choose which to apply to your system prompt.
            </SectionSubtitle>
         </SectionHeader>

         {feedbackEntries.length > 0 ? (
            <>
               <FormSection>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                     <FormLabel style={{ marginBottom: 0 }}>
                        Improvement Suggestions ({feedbackEntries.length})
                     </FormLabel>
                     {actionableEntries.length > 1 && (
                        <SmallBtn $variant="success" onClick={applyAllImprovements} disabled={isImproving}>
                           {isImproving ? (
                              <><FontAwesomeIcon icon={faSpinner} spin /> Generating Combined Improvement...</>
                           ) : (
                              <><FontAwesomeIcon icon={faBrain} /> Improve Prompt from All ({actionableEntries.length})</>
                           )}
                        </SmallBtn>
                     )}
                  </div>

                  <FeedbackList>
                     {feedbackEntries.map(entry => (
                        <FeedbackItem key={entry.id} style={{ opacity: entry.dismissed ? 0.5 : 1 }}>
                           <div className="feedback-icon" style={{
                              background: entry.applied ? '#dcfce7' : entry.dismissed ? '#f1f5f9' : '#fee2e2',
                              color: entry.applied ? '#16a34a' : entry.dismissed ? '#94a3b8' : '#dc2626'
                           }}>
                              <FontAwesomeIcon icon={entry.applied ? faCheck : faThumbsDown} />
                           </div>
                           <div className="feedback-content">
                              <div className="feedback-label" style={{ marginBottom: 4 }}>
                                 User asked: &quot;{entry.userMessage}&quot;
                              </div>
                              <div className="feedback-original">{entry.originalResponse}</div>

                              {entry.isGenerating ? (
                                 <div style={{ padding: '10px 0' }}>
                                    <LoadingDots><span /><span /><span /></LoadingDots>
                                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Generating improvement suggestion...</div>
                                 </div>
                              ) : entry.improvedPrompt ? (
                                 <>
                                    <div className="feedback-improved" style={{
                                       marginTop: 8,
                                       maxHeight: 150,
                                       overflowY: 'auto',
                                       fontSize: 12,
                                       whiteSpace: 'pre-wrap',
                                    }}>
                                       {entry.improvedPrompt}
                                    </div>
                                    {!entry.applied && !entry.dismissed && (
                                       <FeedbackActions>
                                          <SmallBtn $variant="success" onClick={() => applySingleImprovement(entry.id)} style={{ padding: '3px 10px', fontSize: 11 }}>
                                             <FontAwesomeIcon icon={faCheck} /> Apply This
                                          </SmallBtn>
                                          <SmallBtn onClick={() => dismissImprovement(entry.id)} style={{ padding: '3px 10px', fontSize: 11 }}>
                                             Dismiss
                                          </SmallBtn>
                                       </FeedbackActions>
                                    )}
                                    {entry.applied && (
                                       <div style={{ fontSize: 11, color: '#16a34a', marginTop: 6, fontWeight: 500 }}>
                                          <FontAwesomeIcon icon={faCheck} /> Applied to system prompt
                                       </div>
                                    )}
                                    {entry.dismissed && (
                                       <FeedbackActions>
                                          <span style={{ fontSize: 11, color: '#94a3b8' }}>Dismissed</span>
                                          <SmallBtn onClick={() => undoDismiss(entry.id)} style={{ padding: '2px 8px', fontSize: 10 }}>
                                             <FontAwesomeIcon icon={faRotateLeft} /> Undo
                                          </SmallBtn>
                                       </FeedbackActions>
                                    )}
                                 </>
                              ) : null}

                              <div className="feedback-label">Flagged at {entry.timestamp}</div>
                           </div>
                        </FeedbackItem>
                     ))}
                  </FeedbackList>
               </FormSection>

               {showPreview && improvedPromptPreview && (
                  <ImprovementPreview>
                     <div className="preview-header">
                        <FontAwesomeIcon icon={faBrain} />
                        Combined Improvement Preview
                     </div>
                     <div className="preview-section current">
                        <div className="preview-label">Current System Prompt</div>
                        <div className="preview-content">
                           {config.systemPrompt || '(empty)'}
                        </div>
                     </div>
                     <div className="preview-section improved">
                        <div className="preview-label">Improved System Prompt</div>
                        <div className="preview-content">
                           {improvedPromptPreview}
                        </div>
                     </div>
                     <div className="preview-actions">
                        <SmallBtn onClick={rejectImprovedPrompt}>
                           Reject
                        </SmallBtn>
                        <SmallBtn $variant="success" onClick={acceptImprovedPrompt}>
                           <FontAwesomeIcon icon={faCheck} /> Accept & Apply
                        </SmallBtn>
                     </div>
                  </ImprovementPreview>
               )}
            </>
         ) : (
            <FormSection>
               <EmptyState>
                  <div className="empty-icon">🧠</div>
                  <h4>No Feedback Yet</h4>
                  <p>
                     Run the Conversation Simulator and rate responses as &quot;Bad&quot; to trigger self-improvement.
                     The agent will analyze each failure and suggest specific prompt improvements.
                  </p>
                  <div style={{ marginTop: 16 }}>
                     <SmallBtn $variant="primary" onClick={() => setActiveStep(STEP_IDS.CONVERSATION_SIMULATOR)}>
                        <FontAwesomeIcon icon={faFlask} /> Go to Simulator
                     </SmallBtn>
                  </div>
               </EmptyState>
            </FormSection>
         )}

         <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
            <SmallBtn onClick={() => setActiveStep(STEP_IDS.CONVERSATION_SIMULATOR)}>
               <FontAwesomeIcon icon={faArrowLeft} /> Back
            </SmallBtn>
            <SmallBtn $variant="primary" onClick={() => { markStepCompleted(STEP_IDS.SELF_IMPROVEMENT); nextStep(); }}>
               Continue to Export <FontAwesomeIcon icon={faArrowRight} />
            </SmallBtn>
         </div>
      </>
   );};

   const renderStep6 = () => (
      <>
         <SectionHeader>
            <SectionTitle>
               <span className="icon" style={{ background: 'rgba(236,72,153,0.1)', color: '#ec4899' }}>
                  <FontAwesomeIcon icon={faFileExport} />
               </span>
               Export to Workflow
            </SectionTitle>
            <SectionSubtitle>
               Your AI agent is configured and ready to deploy. Export it as a function block into a new or existing Dragify workflow.
            </SectionSubtitle>
         </SectionHeader>

         <FormSection>
            {!exportComplete ? (
               <ExportContainer>
                  <ExportSummary>
                     <div className="summary-item">
                        <span className="check-icon"><FontAwesomeIcon icon={faCheck} /></span>
                        Agent: {config.name || 'Untitled Agent'}
                     </div>
                     <div className="summary-item">
                        <span className="check-icon"><FontAwesomeIcon icon={faCheck} /></span>
                        Model: {MODELS.find(m => m.id === config.model)?.name || config.model}
                     </div>
                     <div className="summary-item">
                        <span className="check-icon"><FontAwesomeIcon icon={faCheck} /></span>
                        Mood: {config.mood ? MOODS.find(m => m.id === config.mood)?.label : 'Default'}
                     </div>
                     <div className="summary-item">
                        <span className="check-icon"><FontAwesomeIcon icon={faCheck} /></span>
                        Temperature: {config.temperature.toFixed(2)}
                     </div>
                     <div className="summary-item">
                        <span className="check-icon"><FontAwesomeIcon icon={faCheck} /></span>
                        Output: {OUTPUT_FORMATS.find(f => f.id === config.outputFormat)?.label || config.outputFormat}
                     </div>
                     <div className="summary-item">
                        <span className="check-icon"><FontAwesomeIcon icon={faCheck} /></span>
                        Memory: {config.memory === 'simple-memory' ? 'Simple Memory' : 'No Memory'}
                     </div>
                  </ExportSummary>

                  <Divider />

                  <FormLabel style={{ marginBottom: 12 }}>
                     <FontAwesomeIcon icon={faLayerGroup} style={{ marginRight: 6, color: '#8b5cf6' }} />
                     Export Destination
                  </FormLabel>

                  <ExportModeSelector>
                     <ExportModeCard
                        $active={exportMode === 'new'}
                        onClick={() => { setExportMode('new'); setSelectedWorkflowId(''); setExportWarnings([]); }}
                     >
                        <div className="mode-icon"><FontAwesomeIcon icon={faPlus} /></div>
                        <div className="mode-label">Create New Workflow</div>
                        <div className="mode-desc">Start a fresh workflow with this agent as the first block</div>
                     </ExportModeCard>
                     <ExportModeCard
                        $active={exportMode === 'existing'}
                        onClick={() => { setExportMode('existing'); setExportWarnings([]); loadWorkflows(); }}
                     >
                        <div className="mode-icon"><FontAwesomeIcon icon={faFolderOpen} /></div>
                        <div className="mode-label">Add to Existing Workflow</div>
                        <div className="mode-desc">Append this agent block to one of your existing workflows</div>
                     </ExportModeCard>
                  </ExportModeSelector>

                  {exportMode === 'existing' && (
                     <>
                        <FormLabel style={{ marginTop: 16, marginBottom: 8 }}>
                           Select a Workflow
                        </FormLabel>
                        {isLoadingWorkflows ? (
                           <div style={{ textAlign: 'center', padding: 20 }}>
                              <LoadingDots><span /><span /><span /></LoadingDots>
                              <p style={{ fontSize: 13, color: '#64748b' }}>Loading workflows...</p>
                           </div>
                        ) : workflows.length > 0 ? (
                           <WorkflowPickerList>
                              {workflows.map(wf => (
                                 <WorkflowPickerItem
                                    key={wf._id}
                                    $active={selectedWorkflowId === wf._id}
                                    onClick={() => setSelectedWorkflowId(wf._id)}
                                 >
                                    <div className="wf-info">
                                       <div className="wf-name">{wf.name || 'Untitled Workflow'}</div>
                                       <div className="wf-meta">
                                          {wf.nodeCount} block{wf.nodeCount !== 1 ? 's' : ''} · Created {new Date(wf.createdAt).toLocaleDateString()}
                                       </div>
                                    </div>
                                    <div className="wf-check">
                                       {selectedWorkflowId === wf._id && <FontAwesomeIcon icon={faCheck} />}
                                    </div>
                                 </WorkflowPickerItem>
                              ))}
                           </WorkflowPickerList>
                        ) : (
                           <EmptyState>
                              <p>No existing workflows found. Create a new one instead.</p>
                           </EmptyState>
                        )}
                     </>
                  )}

                  {exportMode && (
                     <>
                           {preExportWarnings.length > 0 && (
                              <div style={{
                                 marginTop: 16,
                                 border: '1px solid #fde68a',
                                 background: '#fffbeb',
                                 color: '#92400e',
                                 borderRadius: 10,
                                 padding: '10px 12px',
                                 display: 'grid',
                                 gap: 6,
                              }}>
                                 {preExportWarnings.map((warning, index) => (
                                    <div key={`pre-export-warning-${index}`} style={{ fontSize: 12, lineHeight: 1.45 }}>
                                       {warning}
                                    </div>
                                 ))}
                              </div>
                           )}
                        {nameValidationError && !config.name.trim() && (
                           <InlineValidationMessage style={{ marginTop: 16, marginBottom: 0, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto' }}>
                              {nameValidationError}
                           </InlineValidationMessage>
                        )}
                        <ExportButton
                           onClick={handleExport}
                           disabled={isSaving || isExporting || (exportMode === 'existing' && !selectedWorkflowId)}
                           style={{ marginTop: 20 }}
                        >
                           {isExporting ? (
                              <><FontAwesomeIcon icon={faSpinner} spin /> Exporting...</>
                           ) : (
                              <><FontAwesomeIcon icon={faFileExport} /> {exportMode === 'existing' ? 'Add Agent to Workflow' : 'Create Workflow with Agent'}</>
                           )}
                        </ExportButton>
                     </>
                  )}
               </ExportContainer>
            ) : (
               <ExportContainer>
                  <div style={{ fontSize: 60, marginBottom: 20 }}>🎉</div>
                  <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>
                     Agent Exported Successfully!
                  </h2>
                  <p style={{ fontSize: 14, color: '#64748b', marginBottom: 24, maxWidth: 400, margin: '0 auto 24px' }}>
                     Your AI agent <strong>&quot;{config.name || 'Untitled Agent'}&quot;</strong> has been {exportMode === 'existing' ? 'added to the selected workflow' : 'exported to a new workflow'}.
                  </p>

                  <ExportSummary>
                     <div className="summary-item">
                        <span className="check-icon"><FontAwesomeIcon icon={faCheck} /></span>
                        Configuration saved
                     </div>
                     <div className="summary-item">
                        <span className="check-icon"><FontAwesomeIcon icon={faCheck} /></span>
                        System prompt compiled
                     </div>
                     <div className="summary-item">
                        <span className="check-icon"><FontAwesomeIcon icon={faCheck} /></span>
                        Personality applied
                     </div>
                     <div className="summary-item">
                        <span className="check-icon"><FontAwesomeIcon icon={faCheck} /></span>
                        Deployed to workflow
                     </div>
                  </ExportSummary>

                  {exportWarnings.length > 0 && (
                     <div style={{
                        marginTop: 12,
                        border: '1px solid #fde68a',
                        background: '#fffbeb',
                        color: '#92400e',
                        borderRadius: 10,
                        padding: '10px 12px',
                        display: 'grid',
                        gap: 6,
                     }}>
                        {exportWarnings.map((warning, index) => (
                           <div key={`export-warning-${index}`} style={{ fontSize: 12, lineHeight: 1.45 }}>
                              {warning}
                           </div>
                        ))}
                     </div>
                  )}

                  <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 8 }}>
                     {exportedProjectId && (
                        <SmallBtn $variant="primary" onClick={() => window.open(`/workflow/${exportedProjectId}`, '_blank')}>
                           <FontAwesomeIcon icon={faArrowRight} /> Open Workflow
                        </SmallBtn>
                     )}
                     <SmallBtn onClick={() => { setExportComplete(false); setExportMode(null); setSelectedWorkflowId(''); setExportedProjectId(''); setExportWarnings([]); }}>
                        <FontAwesomeIcon icon={faFileExport} /> Export Again
                     </SmallBtn>
                     <SmallBtn onClick={startNewAgent}>
                        <FontAwesomeIcon icon={faRobot} /> Create Another Agent
                     </SmallBtn>
                  </div>
               </ExportContainer>
            )}
         </FormSection>

         {!exportComplete && (
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 16 }}>
               <SmallBtn onClick={() => setActiveStep(STEP_IDS.SELF_IMPROVEMENT)}>
                  <FontAwesomeIcon icon={faArrowLeft} /> Back
               </SmallBtn>
            </div>
         )}
      </>
   );

   const renderActiveStep = () => {
      switch (activeStep) {
         case STEP_IDS.WELCOME: return renderStep1();
         case STEP_IDS.CONFIGURATION: return renderConfigurationStep();
         case STEP_IDS.PERSONALITY_DESIGNER: return renderStep2();
         case STEP_IDS.TOOLS_CONFIGURATION: return renderToolsConfigurationStep();
         case STEP_IDS.PROMPT_STUDIO: return renderStep3();
         case STEP_IDS.CONVERSATION_SIMULATOR: return renderStep4();
         case STEP_IDS.SELF_IMPROVEMENT: return renderStep5();
         case STEP_IDS.EXPORT_TO_WORKFLOW: return renderStep6();
         default: return renderStep1();
      }
   };

   const renderLanding = () => (
      <LandingWrap onClick={() => setActiveCardMenuId(null)}>
         <LandingHeader>
            <LandingTitleBlock>
               <h1>AI Agent Studio</h1>
               <p>Design, test, and manage your AI agents with a unified Dragify experience.</p>
            </LandingTitleBlock>
            <LandingCreateBtn onClick={openStudioForNewAgent}>
               <FontAwesomeIcon icon={faPlus} /> Create Agent
            </LandingCreateBtn>
         </LandingHeader>

         <LandingStats>
            <LandingStatCard>
               <span className="stat-icon"><FontAwesomeIcon icon={faRobot} /></span>
               <div>
                  <h3>{savedAgents.length}</h3>
                  <p>Total Agents</p>
               </div>
            </LandingStatCard>
            <LandingStatCard>
               <span className="stat-icon"><FontAwesomeIcon icon={faBolt} /></span>
               <div>
                  <h3>{activeAgentCount}</h3>
                  <p>Active Agents (7d)</p>
               </div>
            </LandingStatCard>
            <LandingStatCard>
               <span className="stat-icon"><FontAwesomeIcon icon={faClock} /></span>
               <div>
                  <h3>{recentAgentCount}</h3>
                  <p>Recently Updated (48h)</p>
               </div>
            </LandingStatCard>
         </LandingStats>

         <LandingSearchBar>
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <input
               value={searchQuery}
               onChange={e => setSearchQuery(e.target.value)}
               placeholder="Search agents by name, model, or mood..."
            />
         </LandingSearchBar>

         <LandingFilters>
            <LandingFilterBtn $active={agentFilter === 'all'} onClick={() => setAgentFilter('all')}>All Agents</LandingFilterBtn>
            <LandingFilterBtn $active={agentFilter === 'active'} onClick={() => setAgentFilter('active')}>Active</LandingFilterBtn>
            <LandingFilterBtn $active={agentFilter === 'recent'} onClick={() => setAgentFilter('recent')}>Recent</LandingFilterBtn>
         </LandingFilters>

         {isLoadingAgents ? (
            <LandingEmpty>
               <LoadingDots><span /><span /><span /></LoadingDots>
            </LandingEmpty>
         ) : filteredAgents.length > 0 ? (
            <LandingGrid>
               {filteredAgents.map(agent => (
                  <LandingCard
                     key={agent._id}
                     style={{ ['--accent' as any]: new Date().getTime() - new Date(agent.updatedAt).getTime() <= 7 * 24 * 60 * 60 * 1000 ? '#4361ee' : '#f72585' }}
                     onClick={() => openStudioForAgent(agent)}
                  >
                     <div className="card-banner">
                        <AgentOrb
                           name={agent.name || 'Untitled Agent'}
                           model={agent.model || ''}
                           mood={agent.mood || ''}
                        />
                        <span className={`banner-badge${new Date().getTime() - new Date(agent.updatedAt).getTime() <= 7 * 24 * 60 * 60 * 1000 ? ' active' : ''}`}>
                           {new Date().getTime() - new Date(agent.updatedAt).getTime() <= 7 * 24 * 60 * 60 * 1000 ? 'Active' : 'Idle'}
                        </span>
                     </div>
                     <div className="head">
                        <div>
                           <h4>{agent.name || 'Untitled Agent'}</h4>
                           <div className="time-row">
                              <FontAwesomeIcon icon={faClock} /> Updated {new Date(agent.updatedAt).toLocaleDateString()}
                           </div>
                        </div>
                        <div className="card-actions" onClick={e => e.stopPropagation()}>
                           <button
                              type="button"
                              className="menu-btn"
                              aria-label="Agent actions"
                              onClick={() => setActiveCardMenuId(prev => prev === agent._id ? null : agent._id)}
                           >
                              <FontAwesomeIcon icon={faEllipsisVertical} />
                           </button>
                           {activeCardMenuId === agent._id && (
                              <div className="menu-pop">
                                 <button type="button" className="menu-item" onClick={() => openRenameModal(agent)}>
                                    <FontAwesomeIcon icon={faPen} /> Rename
                                 </button>
                                 <button type="button" className="menu-item" onClick={() => duplicateAgent(agent)}>
                                    <FontAwesomeIcon icon={faClone} /> Duplicate
                                 </button>
                                 <button
                                    type="button"
                                    className="menu-item delete"
                                    onClick={() => openDeleteModal(agent)}
                                 >
                                    <FontAwesomeIcon icon={faTrash} /> Delete
                                 </button>
                              </div>
                           )}
                        </div>
                     </div>
                     <div className="chips">
                        <span>{agent.model}</span>
                        <span>{agent.mood || 'default'}</span>
                        <span>{(agent.outputFormat || 'text').toUpperCase()}</span>
                     </div>
                     <div className="foot">
                        <span className="iter-info">⟳ {formatMaxIterationsForCard(agent.maxIterations)} iterations</span>
                        <span>Open <FontAwesomeIcon icon={faArrowRight} /></span>
                     </div>
                  </LandingCard>
               ))}
            </LandingGrid>
         ) : (
            <LandingEmpty>
               <h3>No agents found</h3>
               <p>Try a different search term or create a new AI agent.</p>
               <SmallBtn $variant="primary" onClick={openStudioForNewAgent}>
                  <FontAwesomeIcon icon={faPlus} /> Create Agent
               </SmallBtn>
            </LandingEmpty>
         )}
      </LandingWrap>
   );

   return (
      <PageContainer>
         <TopBar>
            <TopBarLeft>
               <LogoMark>
                  <div className="logo-icon">
                     <FontAwesomeIcon icon={faRobot} />
                  </div>
                  <div className="logo-text">
                     AI Agent <span>Studio</span>
                  </div>
               </LogoMark>
            </TopBarLeft>

            <TopBarRight>
               {viewMode === 'studio' && config.name && (
                  <Tag $color="#06b6d4">
                     <FontAwesomeIcon icon={faRobot} /> {config.name}
                  </Tag>
               )}
               {viewMode === 'studio' ? (
                  <>
                     <TopBarBtn onClick={requestLeaveToLanding}>
                        <FontAwesomeIcon icon={faArrowLeft} /> Library
                     </TopBarBtn>
                     <TopBarBtn onClick={() => { void saveCurrentAgent(); }} disabled={isSaving}>
                        {isSaving ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSave} />} Save
                     </TopBarBtn>
                     <TopBarBtn onClick={() => setActiveStep(STEP_IDS.EXPORT_TO_WORKFLOW)}>
                        <FontAwesomeIcon icon={faFileExport} /> Export
                     </TopBarBtn>
                     <TopBarBtn $primary onClick={() => {
                        if (activeStep < STEPS.length) nextStep();
                        else setActiveStep(STEP_IDS.EXPORT_TO_WORKFLOW);
                     }}>
                        {activeStep < STEPS.length ? (
                           <>Next Step <FontAwesomeIcon icon={faArrowRight} /></>
                        ) : (
                           <>Export Agent <FontAwesomeIcon icon={faFileExport} /></>
                        )}
                     </TopBarBtn>
                  </>
               ) : (
                  <></>
               )}
            </TopBarRight>
         </TopBar>

         {viewMode === 'landing' ? renderLanding() : (
         <BodyLayout>
            <StepsSidebar>
               <SidebarTitle>Build Steps</SidebarTitle>
               {STEPS.map(step => (
                  <StepItem
                     key={step.id}
                     $active={activeStep === step.id}
                     $completed={isStepCompleted(step.id)}
                     onClick={() => goToStep(step.id)}
                  >
                     <StepNumber
                        $active={activeStep === step.id}
                        $completed={isStepCompleted(step.id)}
                     >
                        {isStepCompleted(step.id) ? (
                           <FontAwesomeIcon icon={faCheck} style={{ fontSize: 12 }} />
                        ) : (
                           step.id
                        )}
                     </StepNumber>
                     <StepLabel $active={activeStep === step.id}>{step.label}</StepLabel>
                  </StepItem>
               ))}
            </StepsSidebar>

            <MainContent>
               {renderActiveStep()}
            </MainContent>

            <SavedAgentsPanel>
               <PanelHeader>
                  <span>My Agents</span>
                  <SmallBtn onClick={startNewAgent} style={{ padding: '4px 10px', fontSize: 11 }}>
                     <FontAwesomeIcon icon={faPlus} /> New
                  </SmallBtn>
               </PanelHeader>
               <SavedAgentsList>
                  {isLoadingAgents ? (
                     <div style={{ textAlign: 'center', padding: 20 }}>
                        <LoadingDots><span /><span /><span /></LoadingDots>
                     </div>
                  ) : savedAgents.length > 0 ? (
                     savedAgents.map(agent => (
                        <SavedAgentCard key={agent._id} $active={currentAgentId === agent._id} onClick={() => loadAgent(agent)}>
                           <div className="agent-name">
                              {agent.name || 'Untitled'}
                           </div>
                           <div className="agent-meta">
                              {MODELS.find(m => m.id === agent.model)?.name || agent.model}
                              {agent.mood && ` · ${MOODS.find(m => m.id === agent.mood)?.icon || ''} ${agent.mood}`}
                           </div>
                           <div className="agent-actions">
                              <SmallBtn
                                 $variant="danger"
                                 onClick={e => { e.stopPropagation(); openDeleteModal(agent); }}
                                 style={{ padding: '2px 8px', fontSize: 10 }}
                              >
                                 <FontAwesomeIcon icon={faTrash} /> Delete
                              </SmallBtn>
                           </div>
                        </SavedAgentCard>
                     ))
                  ) : (
                     <div style={{ padding: '20px 12px', textAlign: 'center', fontSize: 12, color: '#94a3b8' }}>
                        No saved agents yet. Configure and save your first agent.
                     </div>
                  )}
               </SavedAgentsList>
            </SavedAgentsPanel>
         </BodyLayout>
         )}

         <CardActionModalOverlay isVisible={isUnsavedLeaveModalOpen} onClick={cancelUnsavedLeave}>
            <CardActionModalContainer onClick={e => e.stopPropagation()}>
               <CardActionModalHeader>
                  <CardActionModalTitle>Unsaved Changes</CardActionModalTitle>
                  <CardActionModalCloseBtn type="button" onClick={cancelUnsavedLeave}>
                     <FontAwesomeIcon icon={faXmark} />
                  </CardActionModalCloseBtn>
               </CardActionModalHeader>

               <p style={{ fontSize: '0.95rem', lineHeight: '1.5', margin: '0 0 1.5rem', color: '#64748b' }}>
                  {UNSAVED_AGENT_LEAVE_MESSAGE}
               </p>

               <CardActionModalActions>
                  <CardActionModalButton type="button" onClick={cancelUnsavedLeave}>No, stay</CardActionModalButton>
                  <CardActionModalButton
                     type="button"
                     $danger
                     onClick={confirmUnsavedLeave}
                  >
                     Yes, leave
                  </CardActionModalButton>
               </CardActionModalActions>
            </CardActionModalContainer>
         </CardActionModalOverlay>

         <CardActionModalOverlay isVisible={isToolMarkdownLeaveModalOpen} onClick={closeToolMarkdownLeaveModal}>
            <CardActionModalContainer onClick={e => e.stopPropagation()}>
               <CardActionModalHeader>
                  <CardActionModalTitle>Markdown Sync Required</CardActionModalTitle>
                  <CardActionModalCloseBtn type="button" onClick={closeToolMarkdownLeaveModal}>
                     <FontAwesomeIcon icon={faXmark} />
                  </CardActionModalCloseBtn>
               </CardActionModalHeader>

               <p style={{ fontSize: '0.95rem', lineHeight: '1.5', margin: '0 0 0.9rem', color: '#64748b' }}>
                  You changed tools configuration, but markdown is not synced with the latest policies.
               </p>

               {toolMarkdownSyncWarnings.length > 0 && (
                  <div style={{ marginBottom: '1rem', paddingLeft: 18 }}>
                     {toolMarkdownSyncWarnings.map((warning, index) => (
                        <div key={`tool-md-modal-warning-${index}`} style={{ marginBottom: 6, color: '#92400e', fontSize: '0.9rem', lineHeight: 1.4 }}>
                           • {warning}
                        </div>
                     ))}
                  </div>
               )}

               <CardActionModalActions>
                  <CardActionModalButton type="button" onClick={closeToolMarkdownLeaveModal}>Stay On Tools</CardActionModalButton>
                  <CardActionModalButton type="button" $danger onClick={continueWithoutToolMarkdownSync}>Continue Anyway</CardActionModalButton>
                  <CardActionModalButton
                     type="button"
                     $primary
                     onClick={() => { void syncMarkdownAndContinueNavigation(); }}
                     disabled={isGeneratingToolMarkdown}
                  >
                     {isGeneratingToolMarkdown ? 'Updating...' : 'Update & Continue'}
                  </CardActionModalButton>
               </CardActionModalActions>
            </CardActionModalContainer>
         </CardActionModalOverlay>

         <CardActionModalOverlay isVisible={isRenameModalOpen} onClick={closeCardActionModals}>
            <CardActionModalContainer onClick={e => e.stopPropagation()}>
               <CardActionModalHeader>
                  <CardActionModalTitle>Rename Agent</CardActionModalTitle>
                  <CardActionModalCloseBtn type="button" onClick={closeCardActionModals}>
                     <FontAwesomeIcon icon={faXmark} />
                  </CardActionModalCloseBtn>
               </CardActionModalHeader>

               <CardActionModalFormGroup>
                  <label htmlFor="renameAgentInput">New name</label>
                  <input
                     id="renameAgentInput"
                     type="text"
                     value={renameDraft}
                     onChange={e => setRenameDraft(e.target.value)}
                     placeholder="Enter a new agent name"
                     autoFocus
                  />
               </CardActionModalFormGroup>

               <CardActionModalActions>
                  <CardActionModalButton type="button" onClick={closeCardActionModals}>Cancel</CardActionModalButton>
                  <CardActionModalButton
                     type="button"
                     $primary
                     onClick={handleRenameSubmit}
                     disabled={!renameDraft.trim()}
                  >
                     Rename
                  </CardActionModalButton>
               </CardActionModalActions>
            </CardActionModalContainer>
         </CardActionModalOverlay>

         <CardActionModalOverlay isVisible={isDeleteModalOpen} onClick={closeCardActionModals}>
            <CardActionModalContainer onClick={e => e.stopPropagation()}>
               <CardActionModalHeader>
                  <CardActionModalTitle>Delete Agent</CardActionModalTitle>
                  <CardActionModalCloseBtn type="button" onClick={closeCardActionModals}>
                     <FontAwesomeIcon icon={faXmark} />
                  </CardActionModalCloseBtn>
               </CardActionModalHeader>

               <p style={{ fontSize: '0.95rem', lineHeight: '1.5', margin: '0 0 1.5rem', color: '#64748b' }}>
                  Are you sure you want to delete <strong>{selectedCardAgent?.name || 'this agent'}</strong>? This action cannot be undone.
               </p>

               <CardActionModalActions>
                  <CardActionModalButton type="button" onClick={closeCardActionModals}>Cancel</CardActionModalButton>
                  <CardActionModalButton
                     type="button"
                     $danger
                     onClick={handleDeleteSubmit}
                  >
                     Delete Permanently
                  </CardActionModalButton>
               </CardActionModalActions>
            </CardActionModalContainer>
         </CardActionModalOverlay>
      </PageContainer>
   );
}